/**
 * SellerLeads — Q Drives Acquisition Pipeline.
 *
 * Operator-grade sourcing terminal:
 *   - Desktop: dense data-rich table with INLINE EXPANDABLE rows (no modal cockpit)
 *   - Mobile: stacked sourcing cards with accordion expansion
 *   - All inline editing: stage, executive, follow-up, offer, valuation (no separate page)
 *   - Bulk select + bulk actions: assign exec, update stage, export Excel, archive
 *   - Advanced filters + global search
 *   - Color-coded temperature (Hot/Warm/Cold/Won/Lost)
 *   - 8 sections in expanded view: Vehicle / Photos / Seller / Negotiation / Pipeline / Notes / Buyers / Audit
 *
 * Goal: scan 50+ leads in seconds, drill into one without losing context.
 */

import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { resolvePhotoUrl } from "@/lib/img";
import { toast } from "sonner";
import {
  CaretRight, CaretDown, Copy, WhatsappLogo, PaperPlaneTilt,
  ImageSquare, FileXls, Archive, Phone, Check, NotePencil, ArrowClockwise,
} from "@phosphor-icons/react";
import {
  PageHeader, Toolbar, ToolbarInput, ToolbarSelect,
  StageBadge, AgeBadge, ACQUISITION_STAGES, fmtINR, timeAgo,
} from "./_shared";
import PhotoGallery from "@/components/admin/PhotoGallery";
import AdminDeleteControls from "@/components/admin/AdminDeleteControls";
import PhoneQuickActions from "@/components/PhoneQuickActions";
import { waLink } from "@/lib/contacts";

// ---------- constants ----------
const TEMP_OPTIONS  = [{ v: "all", l: "All Temp" }, { v: "hot", l: "Hot" }, { v: "warm", l: "Warm" }, { v: "cold", l: "Cold" }];
const STAGE_FILTER  = [{ v: "all", l: "All Stages" }, ...ACQUISITION_STAGES.map(s => ({ v: s, l: s.replace(/_/g, " ") }))];
const URGENCY_FILTER = [{ v: "all", l: "All Urgency" }, { v: "immediate", l: "Immediate" }, { v: "this_week", l: "This Week" }, { v: "this_month", l: "This Month" }, { v: "exploring", l: "Exploring" }];
const FUEL_FILTER   = [{ v: "all", l: "Any Fuel" }, { v: "Petrol", l: "Petrol" }, { v: "Diesel", l: "Diesel" }, { v: "CNG", l: "CNG" }, { v: "Electric", l: "EV" }, { v: "Hybrid", l: "Hybrid" }];
const OWN_FILTER    = [{ v: "all", l: "Any Owner" }, { v: "1", l: "1st" }, { v: "2", l: "2nd" }, { v: "3", l: "3rd+" }];
const HYPO_FILTER   = [{ v: "all", l: "Hyp Any" }, { v: "yes", l: "Hyp Yes" }, { v: "no", l: "Hyp No" }];
const ACC_FILTER    = [{ v: "all", l: "Accident Any" }, { v: "none", l: "None" }, { v: "minor", l: "Minor" }, { v: "major", l: "Major" }];
const SORT_OPTIONS  = [
  { v: "smart", l: "Smart Default" },
  { v: "newest", l: "Newest" },
  { v: "price_desc", l: "Price ↓" },
  { v: "unassigned", l: "Unassigned" },
  { v: "follow_due", l: "Follow-up Due" },
];

// Lead Source canonical enum — matches backend `LEAD_SOURCE_OPTIONS`.
const LEAD_SOURCES = ["Website", "Google", "Facebook", "Instagram", "WhatsApp", "Referral", "Direct Call", "Other"];

const LEAD_SOURCE_FILTER = [{ v: "all", l: "Any Source" }, ...LEAD_SOURCES.map(s => ({ v: s, l: s }))];
// Phase 8.2 — attribution source filter (uses attribution_compact.source)
const ATTR_SOURCE_FILTER = [
  { v: "all",      l: "Any Attribution" },
  { v: "meta",     l: "Meta" },
  { v: "google",   l: "Google" },
  { v: "whatsapp", l: "WhatsApp" },
  { v: "organic",  l: "Organic" },
  { v: "direct",   l: "Direct" },
  { v: "manual",   l: "Manual" },
  { v: "missing",  l: "Missing Attr" },
];
const ATTR_BADGE_TONE = {
  meta:     "bg-blue-500/15 border-blue-500/40 text-blue-200",
  google:   "bg-green-500/15 border-green-500/40 text-green-200",
  whatsapp: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200",
  organic:  "bg-amber-500/10 border-amber-500/30 text-amber-200",
  direct:   "bg-white/5 border-white/15 text-white/55",
  manual:   "bg-violet-500/15 border-violet-500/30 text-violet-200",
  unknown:  "bg-orange-500/10 border-orange-500/30 text-orange-200",
};
function SellerLeadAttrBadge({ attr }) {
  if (!attr || attr.missing) {
    return (
      <div className="text-[9px] text-white/30 italic" data-testid="seller-attr-none">No attr</div>
    );
  }
  const tone = ATTR_BADGE_TONE[attr.source] || ATTR_BADGE_TONE.unknown;
  const adId = attr.ad_id || attr.meta_ad_id;
  return (
    <div className="mt-0.5 space-y-0.5" data-testid={`seller-attr-${attr.source}`}>
      <span className={`inline-block text-[8.5px] font-display tracking-widest uppercase px-1.5 py-0.5 border ${tone}`}>
        {attr.source}
      </span>
      {attr.campaign_name && (
        <div className="text-white/65 text-[9.5px] truncate max-w-[120px]" title={attr.campaign_name}>
          {attr.campaign_name}
        </div>
      )}
      {adId && (
        <div className="text-white/35 text-[9px] font-mono truncate max-w-[120px]" title={adId}>
          ad:{adId}
        </div>
      )}
    </div>
  );
}

// Hot/Warm/Cold accent map driven by urgency + stage.
function tempOf(v) {
  const stage = v.acquisition_stage;
  if (stage === "won") return "won";
  if (stage === "lost") return "lost";
  if (v.urgency === "immediate") return "hot";
  if (v.urgency === "this_week" || v.urgency === "this_month") return "warm";
  return "cold";
}
const TEMP_ACCENT = {
  hot:  { color: "#FF1E1E", label: "HOT", bar: "border-l-[3px] border-[#FF1E1E]" },
  warm: { color: "#F5A623", label: "WARM", bar: "border-l-[3px] border-[#F5A623]" },
  cold: { color: "#808080", label: "COLD", bar: "border-l-[3px] border-[#3A3A3A]" },
  won:  { color: "#22C55E", label: "WON", bar: "border-l-[3px] border-[#22C55E]" },
  lost: { color: "#5A5A5A", label: "LOST", bar: "border-l-[3px] border-[#5A5A5A]" },
};

function lastTouchOf(v) { return v.last_contacted_at || v.created_at; }

// ===================================================================
// TapEdit — inline tap-to-edit number field for mobile cards.
// Tap value → becomes <input> → blur or Enter saves via onSave(int).
// Optimized for thumb editing: large hit area, numeric keypad, no extra UI chrome.
// ===================================================================
function TapEdit({ label, value, onSave, testid, format = (v) => fmtINR(v), placeholder = "—", emphasize = false, valueClassName = "" }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);
  const commit = async () => {
    const n = parseInt(draft, 10) || 0;
    if (n < 0) return setEditing(false);
    if (n !== (value || 0)) {
      await onSave(n);
    }
    setEditing(false);
  };
  return (
    <div className="min-w-0" onClick={(e) => e.stopPropagation()}>
      <div className="text-[9px] uppercase tracking-widest text-white/40">{label}</div>
      {editing ? (
        <input
          autoFocus
          data-testid={`${testid}-input`}
          type="text" inputMode="numeric" pattern="[0-9]*"
          value={draft}
          onChange={(e) => setDraft(e.target.value.replace(/[^\d]/g, ""))}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
          className={`bg-transparent border-b border-[#FF1E1E] outline-none font-display text-[14px] py-0.5 w-full text-white ${valueClassName}`}
        />
      ) : (
        <button
          data-testid={testid}
          onClick={() => setEditing(true)}
          className={`text-left w-full font-display text-[14px] leading-tight py-0.5 ${value ? "text-white" : "text-white/30"} ${emphasize ? "text-[#FF1E1E]" : ""} ${valueClassName}`}
        >
          {value ? format(value) : placeholder}
        </button>
      )}
    </div>
  );
}

// ===================================================================
// MobileLeadCard — thumb-friendly acquisition card.
// Shows the 7 mandated decision fields prominently; supports inline tap-to-edit
// + one-tap Call/WhatsApp/Note actions. Mobile-first per Phase 11–13.
// ===================================================================
function MobileLeadCard({ v, photoUrl, sellerPhoneE164, sellerPhoneForWA, temp, selected, onSelect, onExpand, expanded, onPatch }) {
  // Acquisition decision metrics computed once per render.
  const ready = !!v.ready_to_buy;
  const currentExp = v.current_expected_price ?? v.expected_price ?? 0;

  return (
    <div className="md:hidden">
      {/* Header strip: photo + vehicle + ready badge */}
      <div onClick={onExpand} className="p-3 cursor-pointer">
        <div className="flex items-start gap-2.5">
          <input type="checkbox" checked={selected} onChange={onSelect} onClick={(e) => e.stopPropagation()}
            className="mt-1.5 shrink-0" data-testid={`lead-select-mobile-${v.id}`}/>
          <div className="w-14 h-14 shrink-0 bg-[#0A0A0A] overflow-hidden">
            {photoUrl
              ? <img src={photoUrl} alt="" loading="lazy" className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center text-white/20"><ImageSquare size={14} /></div>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="font-display text-[15px] text-white truncate leading-tight">{v.make} {v.model}</div>
              {ready ? (
                <span data-testid={`ready-badge-${v.id}`}
                  className="text-[9px] font-display tracking-widest uppercase shrink-0 bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 px-1.5 py-0.5 flex items-center gap-1">
                  <Check size={9} weight="bold" /> READY
                </span>
              ) : (
                <span className="text-[9px] font-mono-data tracking-widest uppercase shrink-0" style={{ color: temp.color }}>{temp.label}</span>
              )}
            </div>
            <div className="text-[10px] font-mono-data tracking-wider text-white/40 mt-0.5">{v.registration_number || "—"}</div>
            <div className="text-[10px] text-white/55 mt-0.5">
              {v.year} · {(v.km_driven || 0).toLocaleString()} km · {v.fuel_type}
            </div>
          </div>
        </div>

        {/* Mandated acquisition decision fields — tap-to-edit. Phase 12 + Retail Gap update.
            Order (per updated priority): Current Expected · Offer Given · Gap % · Retail Gap · Liquidity · Ready */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 mt-3 px-1">
          <TapEdit testid={`m-current-${v.id}`} label="Current Expected"
            value={currentExp}
            onSave={(n) => onPatch({ current_expected_price: n })} />
          <TapEdit testid={`m-offer-${v.id}`} label="Offer Given"
            value={v.current_admin_offer}
            onSave={(n) => onPatch({ current_admin_offer: n, note: `Mobile offer ₹${n.toLocaleString()} sent` })}
            emphasize />
          {/* Gap % — negotiation gap between Offer and Current Expected */}
          {(() => {
            const offer = v.current_admin_offer || 0;
            const gapPct = (offer > 0 && currentExp > 0) ? ((offer - currentExp) / currentExp) * 100 : null;
            const cls = gapPct === null ? "text-white/30" : gapPct >= 0 ? "text-emerald-400" : "text-[#FF1E1E]";
            return (
              <div className="min-w-0">
                <div className="text-[9px] uppercase tracking-widest text-white/40">Gap %</div>
                <div className={`font-display text-[14px] leading-tight py-0.5 ${cls}`} data-testid={`m-gappct-${v.id}`}>
                  {gapPct === null ? "—" : `${gapPct >= 0 ? "+" : ""}${gapPct.toFixed(1)}%`}
                </div>
              </div>
            );
          })()}
          {/* Retail Gap — seller expectation vs market resale value */}
          {(() => {
            const retail = v.estimated_retail_value || 0;
            const rg = (retail > 0 && currentExp > 0) ? retail - currentExp : null;
            const cls = rg === null ? "text-white/30" : rg > 0 ? "text-emerald-400" : rg < 0 ? "text-[#FF1E1E]" : "text-white";
            const display = rg === null ? "—" : rg === 0 ? "₹0" : `${rg > 0 ? "+" : ""}${fmtINR(rg)}`;
            return (
              <div className="min-w-0">
                <div className="text-[9px] uppercase tracking-widest text-white/40">Retail Gap</div>
                <div className={`font-display text-[14px] leading-tight py-0.5 ${cls}`} data-testid={`m-retailgap-${v.id}`}>
                  {display}
                </div>
              </div>
            );
          })()}
          <div className="min-w-0" onClick={(e) => e.stopPropagation()}>
            <div className="text-[9px] uppercase tracking-widest text-white/40">Liquidity</div>
            <select
              data-testid={`m-liq-${v.id}`}
              value={v.liquidity_score || ""}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                if (n >= 1 && n <= 10) onPatch({ liquidity_score: n });
              }}
              className="bg-transparent border-b border-white/15 text-white font-display text-[14px] py-0.5 w-full focus:border-[#FF1E1E] outline-none"
            >
              <option value="" className="bg-[#0E0E0E]">—</option>
              {[10,9,8,7,6,5,4,3,2,1].map(n => <option key={n} value={n} className="bg-[#0E0E0E]">{n}{n===10?" · Liquid":n===1?" · Difficult":""}</option>)}
            </select>
          </div>
          <div className="min-w-0" onClick={(e) => e.stopPropagation()}>
            <div className="text-[9px] uppercase tracking-widest text-white/40">Ready To Buy</div>
            <button
              data-testid={`m-ready-${v.id}`}
              onClick={() => onPatch({ ready_to_buy: !ready })}
              className={`mt-0.5 text-[11px] font-display tracking-widest uppercase py-0.5 px-2 border ${
                ready
                  ? "bg-emerald-500/15 border-emerald-500 text-emerald-400"
                  : "bg-transparent border-white/20 text-white/55"
              }`}
            >
              {ready ? "✓ READY" : "Mark Ready"}
            </button>
          </div>
        </div>

        {/* Stage picker — full-width, primary action for the card */}
        <div className="mt-3 pt-2.5 border-t border-white/8" onClick={(e) => e.stopPropagation()}>
          <div className="text-[9px] uppercase tracking-widest text-white/40 mb-1">Current Stage</div>
          <StagePicker stage={v.acquisition_stage || "new_lead"} onPick={(s) => onPatch({ acquisition_stage: s })} testid={`mobile-stage-${v.id}`}/>
        </div>
      </div>

      {/* One-tap action ribbon: Call · WhatsApp · Note · Expand */}
      <div className="flex items-stretch border-t border-white/10 bg-[#0A0A0A]" onClick={(e) => e.stopPropagation()}>
        <a
          data-testid={`m-call-${v.id}`}
          href={sellerPhoneE164 ? `tel:${sellerPhoneE164}` : undefined}
          className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-[11px] font-display tracking-widest uppercase border-r border-white/10 ${
            sellerPhoneE164 ? "text-white active:bg-[#FF1E1E]/15" : "text-white/25 pointer-events-none"
          }`}
        >
          <Phone size={13} weight="bold" /> Call
        </a>
        <a
          data-testid={`m-wa-${v.id}`}
          href={sellerPhoneE164 ? waLink(`Hi ${v.seller_name || "there"}, this is Q Drives following up on your ${v.make} ${v.model}.`, sellerPhoneForWA) : undefined}
          target="_blank" rel="noopener noreferrer"
          className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-[11px] font-display tracking-widest uppercase border-r border-white/10 ${
            sellerPhoneE164 ? "text-emerald-400 active:bg-emerald-500/10" : "text-white/25 pointer-events-none"
          }`}
        >
          <WhatsappLogo size={13} weight="fill" /> WhatsApp
        </a>
        <button
          data-testid={`m-note-${v.id}`}
          onClick={async (e) => {
            e.stopPropagation();
            const text = window.prompt("Quick note:");
            if (text && text.trim()) await onPatch({ note: text.trim() });
          }}
          className="flex-1 flex items-center justify-center gap-1.5 py-3 text-[11px] font-display tracking-widest uppercase border-r border-white/10 text-white active:bg-white/5"
        >
          <NotePencil size={13} weight="bold" /> Note
        </button>
        <button
          data-testid={`m-expand-${v.id}`}
          onClick={onExpand}
          className="px-4 flex items-center justify-center text-white/55 active:bg-white/5"
        >
          {expanded ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
        </button>
      </div>
    </div>
  );
}



// ===================================================================
// Top-level page
// ===================================================================
export default function SellerLeads({ initialStageFilter = "all" }) {
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState([]);
  // Phase 10.3 — server-side true total + has_more flag (replaces silent .to_list(500) cap).
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [pageLimit] = useState(1000);
  // Phase 10.3 — "Hide test/seed leads" toggle (default OFF, no behaviour change for existing users).
  const [excludeNoise, setExcludeNoise] = useState(false);
  const [staffUsers, setStaffUsers] = useState([]);
  const [q, setQ] = useState("");
  const [temp, setTemp] = useState("all");
  const [stage, setStage] = useState(initialStageFilter);
  const [urgency, setUrgency] = useState("all");
  const [fuel, setFuel] = useState("all");
  const [owners, setOwners] = useState("all");
  const [hypo, setHypo] = useState("all");
  const [accident, setAccident] = useState("all");
  const [source, setSource] = useState("all");
  const [executive, setExecutive] = useState("");
  const [myOnly, setMyOnly] = useState(false);
  const [sort, setSort] = useState("newest");
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Phase 8.2 — client-side attribution filter (data already in response payload)
  const [attrSourceFilter, setAttrSourceFilter] = useState("all");

  const load = useCallback(() => {
    // Phase 10.3 — request paginated response and respect the Hide-test/seed toggle.
    return api.get("/admin/seller-leads", {
      params: { limit: pageLimit, offset: 0, exclude_noise: excludeNoise ? "true" : "false" },
    })
      .then(r => {
        const payload = r.data;
        // Support both new {items,total,…} shape and legacy array (defensive).
        if (Array.isArray(payload)) {
          setItems(payload);
          setTotalCount(payload.length);
          setHasMore(false);
        } else {
          setItems(payload.items || []);
          setTotalCount(payload.total ?? (payload.items || []).length);
          setHasMore(Boolean(payload.has_more));
        }
      })
      .catch(err => {
        // Phase 10.3 — surface silent failures (was previously swallowed).
        // eslint-disable-next-line no-console
        console.error("seller-leads load failed", err);
      });
  }, [pageLimit, excludeNoise]);
  // Phase 10.3 — refetch whenever pageLimit or excludeNoise change.
  // Depend on the primitive state values directly (not `load`'s function identity)
  // so the effect fires reliably even if useCallback memoization is short-circuited.
  useEffect(() => { load(); }, [load, pageLimit, excludeNoise]);

  // Load staff users once — used for the Assigned-To dropdown.
  useEffect(() => {
    api.get("/admin/users").then(r => setStaffUsers(r.data || [])).catch(() => setStaffUsers([]));
  }, []);

  // ---------- filter + sort ----------
  const filtered = useMemo(() => {
    let list = [...items];
    if (q) {
      const Q = q.toLowerCase();
      list = list.filter(v =>
        `${v.make} ${v.model} ${v.variant} ${v.registration_number} ${v.seller_phone} ${v.assigned_executive}`
          .toLowerCase().includes(Q));
    }
    if (temp !== "all") list = list.filter(v => tempOf(v) === temp);
    if (stage !== "all") list = list.filter(v => (v.acquisition_stage || "new_lead") === stage);
    if (urgency !== "all") list = list.filter(v => v.urgency === urgency);
    if (fuel !== "all") list = list.filter(v => v.fuel_type === fuel);
    if (owners !== "all") {
      list = list.filter(v => owners === "3" ? Number(v.owners) >= 3 : String(v.owners) === owners);
    }
    if (hypo !== "all") list = list.filter(v => Boolean(v.hypothecation) === (hypo === "yes"));
    if (accident !== "all") list = list.filter(v => v.accident_history === accident);
    if (source !== "all") list = list.filter(v => (v.source || "Website") === source);
    // Phase 8.2 — attribution source filter (client-side; uses payload field)
    if (attrSourceFilter !== "all") {
      list = list.filter(v => {
        const ac = v.attribution_compact;
        if (attrSourceFilter === "missing") return !ac || ac.missing;
        return (ac?.source || "") === attrSourceFilter;
      });
    }
    if (executive.trim()) {
      const E = executive.trim().toLowerCase();
      list = list.filter(v => (v.assigned_executive || "").toLowerCase().includes(E));
    }
    if (myOnly && currentUser?.id) {
      list = list.filter(v => v.assigned_to_user_id === currentUser.id);
    }

    switch (sort) {
      case "newest":
        list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); break;
      case "price_desc":
        list.sort((a, b) => (b.expected_price || 0) - (a.expected_price || 0)); break;
      case "unassigned":
        list.sort((a, b) => Number(!!a.assigned_executive) - Number(!!b.assigned_executive)
                          || new Date(b.created_at) - new Date(a.created_at)); break;
      case "follow_due":
        list.sort((a, b) => {
          const aDue = a.next_follow_up_at ? new Date(a.next_follow_up_at) : new Date(8e15);
          const bDue = b.next_follow_up_at ? new Date(b.next_follow_up_at) : new Date(8e15);
          return aDue - bDue;
        }); break;
      default: // smart: immediate > newest > unassigned > value
        list.sort((a, b) => {
          const ai = a.urgency === "immediate" ? 0 : 1;
          const bi = b.urgency === "immediate" ? 0 : 1;
          if (ai !== bi) return ai - bi;
          if (!!a.assigned_executive !== !!b.assigned_executive) return Number(!!a.assigned_executive) - Number(!!b.assigned_executive);
          const dt = new Date(b.created_at) - new Date(a.created_at);
          if (dt !== 0) return dt;
          return (b.expected_price || 0) - (a.expected_price || 0);
        });
    }
    return list;
  }, [items, q, temp, stage, urgency, fuel, owners, hypo, accident, source, executive, myOnly, currentUser, sort, attrSourceFilter]);

  // ---------- counts strip ----------
  const counts = useMemo(() => {
    const c = { total: items.length, hot: 0, warm: 0, cold: 0, won: 0, lost: 0, unassigned: 0 };
    items.forEach(v => {
      c[tempOf(v)]++;
      if (!v.assigned_executive) c.unassigned++;
    });
    return c;
  }, [items]);

  // ---------- expand / select ----------
  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleSelect = (id, e) => {
    e?.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAllFiltered = () => {
    setSelectedIds(prev => prev.size === filtered.length ? new Set() : new Set(filtered.map(v => v.id)));
  };
  const clearSelection = () => setSelectedIds(new Set());

  // ---------- inline edit (shared by row + expanded view + bulk) ----------
  const patchVehicle = async (vid, body) => {
    try {
      await api.patch(`/admin/vehicles/${vid}/acquisition`, body);
      // Update local state so UI updates instantly (no re-fetch round-trip needed)
      setItems(prev => prev.map(v => v.id === vid ? { ...v, ...body } : v));
    } catch (e) {
      toast.error("Update failed: " + (e?.response?.data?.detail || e.message));
      throw e;
    }
  };

  // ---------- export ----------
  const downloadXlsx = async (params, label) => {
    setExporting(true);
    const t = toast.loading(`Preparing ${label} export…`);
    try {
      const res = await api.get("/admin/vehicles/export.xlsx", { params, responseType: "blob", timeout: 60000 });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T-]/g, "");
      const a = document.createElement("a");
      a.href = url; a.download = `qdrives-pipeline-${stamp}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${label}`, { id: t });
    } catch (e) {
      toast.error(`Export failed: ${e?.message || "unknown"}`, { id: t });
    } finally {
      setExporting(false);
    }
  };

  // ---------- bulk actions ----------
  const bulkAssign = async () => {
    const exec = window.prompt("Assign these leads to which executive? (blank to unassign)");
    if (exec === null) return;
    const ids = Array.from(selectedIds);
    const t = toast.loading(`Updating ${ids.length} leads…`);
    let ok = 0;
    for (const id of ids) {
      try { await patchVehicle(id, { assigned_executive: exec.trim() || null }); ok++; } catch { /* keep going */ }
    }
    toast.success(`${ok}/${ids.length} updated`, { id: t });
    clearSelection();
  };
  const bulkStage = async () => {
    const stages = ACQUISITION_STAGES.join(" / ");
    const s = window.prompt(`Bulk update stage to:\n${stages}`, "contacted");
    if (!s || !ACQUISITION_STAGES.includes(s)) return toast.error("Invalid stage");
    const ids = Array.from(selectedIds);
    const t = toast.loading(`Updating ${ids.length} leads…`);
    let ok = 0;
    for (const id of ids) {
      try { await patchVehicle(id, { acquisition_stage: s }); ok++; } catch { /* keep going */ }
    }
    toast.success(`${ok}/${ids.length} updated`, { id: t });
    clearSelection();
  };

  return (
    <div data-testid="admin-seller-leads">
      <PageHeader
        title="Acquisition Pipeline"
        subtitle={
          // Phase 10.3 — show TRUE total from the backend (was previously capped at 500).
          // `filtered.length` is the post-client-filter slice, `items.length` is the page
          // slice returned by the API, and `totalCount` is the unfiltered DB total.
          items.length < totalCount
            ? `${filtered.length} of ${items.length} shown · ${totalCount} total${excludeNoise ? " (test/seed hidden)" : ""}${hasMore ? " · more available" : ""}`
            : `${filtered.length} of ${totalCount} leads${excludeNoise ? " (test/seed hidden)" : ""}`
        }
        right={
          <button
            onClick={() => downloadXlsx({ exclude_noise: excludeNoise ? "true" : "false" }, excludeNoise ? "real leads only" : "all leads")}
            disabled={exporting} data-testid="lead-export-all"
            className="btn-secondary text-[10px] py-2 px-3 inline-flex items-center gap-1.5">
            <FileXls size={12} weight="bold" />Export Excel
          </button>
        }/>

      {/* Counts strip — quick situational awareness */}
      <CountsStrip counts={counts} active={temp} onSelect={(t) => setTemp(t === temp ? "all" : t)} />

      {/* Toolbar */}
      <div className="bg-[#0E0E0E] p-3 sm:p-4 mb-3 sticky top-0 z-20">
        <Toolbar>
          <ToolbarInput value={q} onChange={setQ} placeholder="Search reg / brand / model / phone / exec…" testid="leads-search" />
          <ToolbarSelect value={stage} onChange={setStage} testid="leads-stage" options={STAGE_FILTER} />
          <ToolbarSelect value={urgency} onChange={setUrgency} testid="leads-urgency" options={URGENCY_FILTER} />
          <ToolbarSelect value={sort} onChange={setSort} testid="leads-sort" options={SORT_OPTIONS} />
          <button
            onClick={() => setMyOnly(m => !m)}
            data-testid="leads-my-only"
            className={`text-[10px] tracking-widest uppercase font-bold py-2 px-3 border transition-colors ${
              myOnly
                ? "border-[#FF1E1E] bg-[#FF1E1E]/15 text-[#FF1E1E]"
                : "border-[#2A2A2A] text-white/65 hover:border-white/30"
            }`}
          >
            {myOnly ? "✓ My Leads" : "My Leads"}
          </button>
          {/* Phase 10.3 — Hide test/seed leads toggle. Default OFF. */}
          <button
            onClick={() => setExcludeNoise(v => !v)}
            data-testid="leads-hide-test-seed"
            title="Hide QA / dummy / bulk-seed leads (server-side filter)"
            className={`text-[10px] tracking-widest uppercase font-bold py-2 px-3 border transition-colors ${
              excludeNoise
                ? "border-amber-500 bg-amber-500/15 text-amber-300"
                : "border-[#2A2A2A] text-white/65 hover:border-white/30"
            }`}
          >
            {excludeNoise ? "✓ Hide Test/Seed" : "Hide Test/Seed"}
          </button>
          <button onClick={() => setShowFilters(s => !s)} className="btn-secondary text-[10px] py-2 px-3" data-testid="leads-toggle-filters">
            {showFilters ? "Hide Filters" : "More Filters"}
          </button>
        </Toolbar>
        {showFilters && (
          <Toolbar>
            <ToolbarSelect value={fuel} onChange={setFuel} testid="leads-fuel" options={FUEL_FILTER} />
            <ToolbarSelect value={owners} onChange={setOwners} testid="leads-owners" options={OWN_FILTER} />
            <ToolbarSelect value={hypo} onChange={setHypo} testid="leads-hypo" options={HYPO_FILTER} />
            <ToolbarSelect value={accident} onChange={setAccident} testid="leads-accident" options={ACC_FILTER} />
            <ToolbarSelect value={source} onChange={setSource} testid="leads-source" options={LEAD_SOURCE_FILTER} />
            <ToolbarSelect value={attrSourceFilter} onChange={setAttrSourceFilter} testid="leads-attr-source" options={ATTR_SOURCE_FILTER} />
            <ToolbarInput value={executive} onChange={setExecutive} placeholder="Executive…" testid="leads-exec-filter" />
          </Toolbar>
        )}
        {selectedIds.size > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-white/65 font-mono-data">{selectedIds.size} selected</span>
            <button onClick={bulkAssign} data-testid="bulk-assign" className="btn-secondary text-[10px] py-1.5 px-2.5">Assign Exec</button>
            <button onClick={bulkStage} data-testid="bulk-stage" className="btn-secondary text-[10px] py-1.5 px-2.5">Set Stage</button>
            <button onClick={() => downloadXlsx({ ids: Array.from(selectedIds).join(",") }, `${selectedIds.size} selected`)} data-testid="bulk-export" className="btn-secondary text-[10px] py-1.5 px-2.5">Export Selected</button>
            <button onClick={clearSelection} className="text-white/45 text-[11px] hover:text-white">Clear</button>
          </div>
        )}
        {/* Destructive actions — always visible so super admin can use Delete All without selecting first */}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <AdminDeleteControls
            resource="leads"
            selectedIds={selectedIds}
            totalCount={items.length}
            bulkEndpoint="/admin/vehicles/bulk-delete"
            allEndpoint="/admin/vehicles/all"
            testidPrefix="leads"
            onDeleted={() => { clearSelection(); load(); }}
          />
        </div>
      </div>

      {/* DESKTOP: data-dense rows (tight 1px gap). MOBILE: stacked cards with
          16px breathing room + per-card border so each lead reads as a
          self-contained box and Call/WhatsApp/Note actions clearly belong to
          their own card. */}
      <div className="space-y-3 md:space-y-px">
        {/* Select-all header (desktop only) */}
        {filtered.length > 0 && (
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-[#0A0A0A] text-[10px] tracking-widest uppercase text-white/35 font-mono-data">
            <input type="checkbox" checked={selectedIds.size === filtered.length && filtered.length > 0}
              onChange={selectAllFiltered} data-testid="leads-select-all"
              className="mr-1"/>
            <div className="w-12">PHOTO</div>
            <div className="flex-1 min-w-0">VEHICLE</div>
            <div className="w-40 hidden lg:block">SPECS</div>
            <div className="w-32 text-right">PRICE</div>
            <div className="w-20 hidden lg:block">URGENCY</div>
            <div className="w-32 hidden xl:block">SELLER</div>
            <div className="w-28">STAGE</div>
            <div className="w-16">AGE</div>
            <div className="w-8" />
          </div>
        )}

        {filtered.map(v => (
          <LeadRow
            key={v.id}
            v={v}
            staffUsers={staffUsers}
            currentUser={currentUser}
            expanded={expandedIds.has(v.id)}
            selected={selectedIds.has(v.id)}
            onExpand={() => toggleExpand(v.id)}
            onSelect={(e) => toggleSelect(v.id, e)}
            onPatch={(body) => patchVehicle(v.id, body)}
            onRefresh={load}
          />
        ))}

        {filtered.length === 0 && (
          <div className="text-center text-white/40 py-12 text-sm">No leads match your filters.</div>
        )}
      </div>
    </div>
  );
}

// ===================================================================
// CountsStrip — quick visual census
// ===================================================================
function CountsStrip({ counts, active, onSelect }) {
  const tiles = [
    { k: "hot",  label: "HOT",   color: TEMP_ACCENT.hot.color },
    { k: "warm", label: "WARM",  color: TEMP_ACCENT.warm.color },
    { k: "cold", label: "COLD",  color: TEMP_ACCENT.cold.color },
    { k: "won",  label: "WON",   color: TEMP_ACCENT.won.color },
    { k: "lost", label: "LOST",  color: TEMP_ACCENT.lost.color },
  ];
  return (
    <div className="grid grid-cols-5 gap-px mb-3" data-testid="leads-counts-strip">
      {tiles.map(t => (
        <button key={t.k} onClick={() => onSelect(t.k)}
          data-testid={`counts-${t.k}`}
          className={`bg-[#0E0E0E] px-2 sm:px-3 py-2 text-left transition-opacity ${active === t.k ? "" : "opacity-70 hover:opacity-100"}`}>
          <div className="text-[9px] tracking-widest font-mono-data uppercase" style={{ color: t.color }}>{t.label}</div>
          <div className="text-xl sm:text-2xl font-bold text-white leading-tight mt-0.5">{counts[t.k]}</div>
        </button>
      ))}
    </div>
  );
}

// ===================================================================
// LeadRow — responsive row (desktop) + card (mobile), expandable
// ===================================================================
function LeadRow({ v, staffUsers, currentUser, expanded, selected, onExpand, onSelect, onPatch, onRefresh }) {
  const t = TEMP_ACCENT[tempOf(v)];
  const photoUrl = (v.photos_signed?.[0]?.url || v.photos?.[0])
    ? resolvePhotoUrl(v.photos_signed?.[0] || v.photos[0])
    : null;
  const sellerPhoneE164 = (v.seller_phone || "").replace(/[^\d+]/g, "");
  const sellerPhoneForWA = sellerPhoneE164.replace(/^\+/, "");

  return (
    <div
      className={`bg-[#0E0E0E] ${t.bar} ${expanded ? "ring-1 ring-[#FF1E1E]/30" : ""} hover:bg-[#121212] transition-colors border border-[#1F1F1F] md:border-0 md:border-b md:border-[#1A1A1A] rounded md:rounded-none shadow-sm md:shadow-none`}
      data-testid={`lead-row-${v.id}`}
    >
      {/* ============== COLLAPSED HEADER ============== */}
      {/* DESKTOP layout (md+) */}
      <div onClick={onExpand} className="hidden md:flex items-center gap-2 px-3 py-2 cursor-pointer">
        <input type="checkbox" checked={selected} onChange={onSelect} onClick={(e) => e.stopPropagation()}
          data-testid={`lead-select-${v.id}`} className="mr-1"/>
        {/* PHOTO */}
        <div className="w-12 h-12 shrink-0 bg-[#0A0A0A] overflow-hidden">
          {photoUrl
            ? <img src={photoUrl} alt="" loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            : <div className="w-full h-full flex items-center justify-center text-white/20"><ImageSquare size={16} /></div>}
        </div>
        {/* VEHICLE */}
        <div className="flex-1 min-w-0">
          <div className="font-display text-sm text-white truncate">
            {v.make} <span className="text-white/85">{v.model}</span>
            {v.variant && <span className="text-white/45 text-[11px] ml-1.5">· {v.variant}</span>}
          </div>
          <div className="text-[10px] font-mono-data tracking-wider text-white/45 mt-0.5">
            {v.registration_number || "—"}
          </div>
        </div>
        {/* SPECS */}
        <div className="w-40 hidden lg:block text-[10px] font-mono-data text-white/55 leading-tight">
          {v.year || "—"} · {(v.km_driven || 0).toLocaleString()} km
          <div className="text-white/40 mt-0.5">{v.fuel_type} · {v.transmission} · {v.owners}-own</div>
        </div>
        {/* PRICE — current expected + offer stacked */}
        <div className="w-32 text-right">
          <div className="font-display text-sm text-white">{fmtINR(v.current_expected_price ?? v.expected_price)}</div>
          {v.current_admin_offer && (
            <div className="text-[10px] text-[#FF1E1E] font-mono-data tracking-wider">offer {fmtINR(v.current_admin_offer)}</div>
          )}
        </div>
        {/* GAP % (offer vs current expected) + RETAIL GAP — acquisition decision metrics (lg+) */}
        <div className="w-28 hidden lg:block text-right text-[10px] font-mono-data leading-tight">
          {(() => {
            const offer  = v.current_admin_offer || 0;
            const curExp = v.current_expected_price ?? v.expected_price ?? 0;
            const retail = v.estimated_retail_value || 0;
            const gapPct = (offer > 0 && curExp > 0) ? ((offer - curExp) / curExp) * 100 : null;
            const retailGap = (retail > 0 && curExp > 0) ? retail - curExp : null;
            const pctCls = gapPct === null ? "text-white/30" : gapPct >= 0 ? "text-emerald-400" : "text-[#FF1E1E]";
            const rgCls  = retailGap === null ? "text-white/30" : retailGap > 0 ? "text-emerald-400" : retailGap < 0 ? "text-[#FF1E1E]" : "text-white";
            const rgDisplay = retailGap === null ? "—" : retailGap === 0 ? "₹0" : `${retailGap > 0 ? "+" : ""}${fmtINR(retailGap)}`;
            return (
              <>
                <div className={pctCls} data-testid={`d-gappct-${v.id}`}>
                  gap {gapPct === null ? "—" : `${gapPct >= 0 ? "+" : ""}${gapPct.toFixed(1)}%`}
                </div>
                <div className={rgCls} data-testid={`d-retailgap-${v.id}`}>
                  ret {rgDisplay}
                </div>
              </>
            );
          })()}
        </div>
        {/* LIQUIDITY + READY badge (xl+ to preserve readability) */}
        <div className="w-20 hidden xl:flex flex-col items-end gap-0.5 text-[10px] font-mono-data">
          <div className="text-white/65">L:{v.liquidity_score || "—"}</div>
          {v.ready_to_buy && (
            <span className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 px-1 leading-tight tracking-widest uppercase text-[9px]">
              ✓ READY
            </span>
          )}
        </div>
        {/* URGENCY */}
        <div className="w-20 hidden lg:block text-[10px] tracking-widest uppercase font-mono-data" style={{ color: t.color }}>
          {v.urgency || "—"}
        </div>
        {/* SELLER */}
        <div className="w-32 hidden xl:block text-[11px] font-mono-data text-white/65 truncate">
          {v.seller_phone || "—"}
          {/* Phase 8.2 — compact attribution row badge */}
          <SellerLeadAttrBadge attr={v.attribution_compact} />
          {/* Phase 7.3 — WhatsApp Intake provenance badge */}
          {v.promoted_from_whatsapp_intake && (
            <div className="mt-0.5" data-testid={`seller-wa-intake-${v.id}`}>
              <span className="inline-block text-[8.5px] font-display tracking-widest uppercase px-1.5 py-0.5 border bg-violet-500/15 border-violet-500/40 text-violet-200">
                WA Intake
              </span>
            </div>
          )}
        </div>
        {/* STAGE */}
        <div className="w-28" onClick={(e) => e.stopPropagation()}>
          <StagePicker stage={v.acquisition_stage || "new_lead"} onPick={(s) => onPatch({ acquisition_stage: s })} testid={`row-stage-${v.id}`} />
        </div>
        {/* AGE */}
        <div className="w-16 text-[10px] text-white/40">{timeAgo(lastTouchOf(v))}</div>
        {/* EXPAND */}
        <div className="w-8 text-white/35 flex justify-end">
          {expanded ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
        </div>
      </div>

      {/* MOBILE layout (< md) — acquisition decision card with tap-to-edit + one-tap actions.
          Designed for thumb-driven dealership use: every key field visible without scrolling
          inside the card; tap any price/score value to edit inline. */}
      <MobileLeadCard
        v={v}
        photoUrl={photoUrl}
        sellerPhoneE164={sellerPhoneE164}
        sellerPhoneForWA={sellerPhoneForWA}
        temp={t}
        selected={selected}
        onSelect={onSelect}
        onExpand={onExpand}
        expanded={expanded}
        onPatch={onPatch}
      />

      {/* ============== EXPANDED — NEW HYBRID LAYOUT ==============
          Hero → Workflow → (Tabs ⇄ permanent Negotiation rail on lg+) → Sticky Action Bar
          See LeadDetailPanel for the orchestration. */}
      {expanded && (
        <LeadDetailPanel
          v={v}
          staffUsers={staffUsers}
          currentUser={currentUser}
          onPatch={onPatch}
          onRefresh={onRefresh}
          sellerPhoneE164={sellerPhoneE164}
          sellerPhoneForWA={sellerPhoneForWA}
        />
      )}
    </div>
  );
}

// ===================================================================
// LeadDetailPanel — the new acquisition-first workspace.
// Layout: Sticky Header → Hero → Workflow → (Tabs ⇄ permanent Negotiation rail on lg+) → Sticky Actions
// ===================================================================
function LeadDetailPanel({ v, staffUsers, currentUser, onPatch, onRefresh, sellerPhoneE164, sellerPhoneForWA }) {
  // 4 tabs on mobile (incl. Negotiation), 3 on desktop (Negotiation is permanently visible in the rail).
  const [tab, setTab] = useState("details");
  const noteAnchorRef = useRef(null);
  const offerAnchorRef = useRef(null);

  const sellerWaText = `Hi ${v.seller_name || "there"}, this is Q Drives following up on your ${v.make} ${v.model}.`;
  const callHref = sellerPhoneE164 ? `tel:${sellerPhoneE164}` : null;
  const waHref = sellerPhoneForWA ? waLink(sellerWaText, sellerPhoneForWA) : null;

  // Quick-jump helpers used by the bottom sticky action bar so the operator can
  // get to the right tab + scroll target in a single tap.
  const jumpToOffer = () => {
    // Negotiation tab on mobile; on desktop it's already visible in the right rail.
    setTab("negotiation");
    // Wait for tab render before scrolling.
    setTimeout(() => offerAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  };
  const jumpToNote = () => {
    setTab("history");
    setTimeout(() => noteAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  };

  return (
    <div data-testid={`lead-expanded-${v.id}`} className="bg-[#080808] border-t border-white/8">
      {/* === STICKY LEAD HEADER (always visible while scrolling the expansion) === */}
      <StickyLeadHeader v={v} staffUsers={staffUsers} />

      <div className="p-3 sm:p-5 pb-24 lg:pb-5 space-y-5">
        {/* === SECTION 1 — ACQUISITION HERO === */}
        <AcquisitionHero
          v={v}
          callHref={callHref}
          waHref={waHref}
          staffUsers={staffUsers}
          currentUser={currentUser}
          onPatch={onPatch}
        />

        {/* === SECTION 2 — WORKFLOW BAND ===
            Reuses the existing PipelineSection (Stage, Assigned, Follow-up, Owner,
            Created By, Last Updated By + Mark Won/Lost/Archive). */}
        <PipelineSection v={v} staffUsers={staffUsers} currentUser={currentUser} onPatch={onPatch} />

        {/* === SECTION 3 — MAIN ===
            Mobile (<lg): 5-tab UI (Details/Negotiation/Media/Buyers/History).
            Desktop (≥lg): 3-column workspace, no tabs — everything visible on one screen. */}
        <div className="lg:hidden">
          <TabBar v={v} active={tab} onSelect={setTab} />
          <div className="mt-4 space-y-5">
            {tab === "details" && (
              <>
                <VehicleSection v={v} />
                <SellerSection v={v} phoneE164={sellerPhoneE164} phoneForWA={sellerPhoneForWA} onPatch={onPatch} />
                <CollapsibleCard title="Pricing / Acquisition Inputs" defaultOpen testid={`collapse-ai-pricing-mobile-${v.id}`}>
                  <PricingInputsStub />
                </CollapsibleCard>
                <CollapsibleCard title="Acquisition Strategy" testid={`collapse-acquisition-mobile-${v.id}`}>
                  <div className="space-y-4">
                    <ProfitSummary v={v} />
                    <ProfitabilitySection v={v} onPatch={onPatch} onRefresh={onRefresh} />
                  </div>
                </CollapsibleCard>
              </>
            )}
            {tab === "negotiation" && (
              <div className="space-y-5">
                <div ref={offerAnchorRef} />
                <NegotiationWorkspace v={v} onPatch={onPatch} onRefresh={onRefresh} />
              </div>
            )}
            {tab === "media" && <MediaSection v={v} />}
            {tab === "buyers" && <SuggestedBuyersSection v={v} />}
            {tab === "history" && (
              <>
                <div ref={noteAnchorRef} />
                <OfferHistorySection v={v} />
                <NotesSection v={v} onPatch={onPatch} />
                <AuditHistorySection v={v} />
              </>
            )}
          </div>
        </div>

        {/* Desktop — vertical stacked sections following the natural acquisition workflow:
            Vehicle → Seller → AI Pricing Engine → Acquisition Strategy → Negotiation → History.
            Replaces the older 3-column layout per spec: use full page width, no narrow side rail. */}
        <div
          className="hidden lg:block space-y-5"
          data-testid={`workspace-stacked-${v.id}`}
        >
          {/* 1) Vehicle Details — expanded by default */}
          <div data-testid={`workspace-section-vehicle-${v.id}`}>
            <VehicleSection v={v} />
          </div>

          {/* 2) Seller Details — expanded by default */}
          <div data-testid={`workspace-section-seller-${v.id}`}>
            <SellerSection v={v} phoneE164={sellerPhoneE164} phoneForWA={sellerPhoneForWA} onPatch={onPatch} />
          </div>

          {/* 3) Pricing / Acquisition Inputs — manual entry only. Claude AI engine retired 2026-06-23 */}
          <CollapsibleCard title="Pricing / Acquisition Inputs" defaultOpen testid={`collapse-ai-pricing-desk-${v.id}`}>
            <PricingInputsStub />
          </CollapsibleCard>

          {/* 4) Acquisition Strategy — collapsed by default. Answers "should I buy and at what price?"
              Bundles Profitability KPIs + Acquisition Inputs per spec. */}
          <CollapsibleCard title="Acquisition Strategy" testid={`collapse-acquisition-desk-${v.id}`}>
            <div className="space-y-4">
              <ProfitSummary v={v} />
              <ProfitabilitySection v={v} onPatch={onPatch} onRefresh={onRefresh} />
            </div>
          </CollapsibleCard>

          {/* Negotiation + Offer History — kept functional, placed after the review/decision flow */}
          <div ref={offerAnchorRef} />
          <NegotiationCore v={v} onPatch={onPatch} />
          <OfferHistorySection v={v} />

          {/* Suggested Buyers — secondary panel, retained */}
          <SuggestedBuyersSection v={v} />
        </div>
      </div>

      {/* === STICKY BOTTOM ACTION BAR (mobile-priority) === */}
      <StickyActionBar
        v={v}
        callHref={callHref}
        waHref={waHref}
        onOffer={jumpToOffer}
        onNote={jumpToNote}
      />
    </div>
  );
}

// -------------------------------------------------------------------
// Sticky lead header — top condensed band. Acts as a "you-are-here"
// strip when the operator scrolls past the hero.
// -------------------------------------------------------------------
function StickyLeadHeader({ v, staffUsers }) {
  const assignedStaff = v.assigned_to_user_id ? (staffUsers || []).find(u => u.id === v.assigned_to_user_id) : null;
  const ownerLabel = assignedStaff?.name || assignedStaff?.phone || v.assigned_executive || "Unassigned";
  const stage = (v.acquisition_stage || "new_lead").replace(/_/g, " ");
  const expected = v.current_expected_price ?? v.expected_price;
  return (
    <div
      className="sticky top-0 z-30 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-white/10 px-3 sm:px-5 py-2 flex items-center gap-3 text-xs"
      data-testid={`sticky-header-${v.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="font-display text-sm text-white truncate">
          {v.make} <span className="text-white/85">{v.model}</span>
          {v.variant && <span className="text-white/45 ml-1.5 text-[11px]">· {v.variant}</span>}
        </div>
        <div className="text-[10px] font-mono-data text-white/55 tracking-wider truncate">
          <span className="uppercase text-[#FF1E1E]">{stage}</span>
          <span className="text-white/30 mx-1">·</span>
          <span>{ownerLabel}</span>
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-display text-sm text-white leading-tight">{fmtINR(expected)}</div>
        <div className="text-[10px] font-mono-data text-[#FF1E1E] tracking-wider">
          offer {v.current_admin_offer ? fmtINR(v.current_admin_offer) : "—"}
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// AcquisitionHero — Section 1, fits within the first mobile screen.
// Answers Qs 1–5: vehicle, spec, prices, retail gap, liquidity, stage, owner,
// + quick-actions CALL / WHATSAPP / ASSIGN.
// -------------------------------------------------------------------
function AcquisitionHero({ v, callHref, waHref, staffUsers, currentUser, onPatch }) {
  const expected = v.current_expected_price ?? v.expected_price ?? 0;
  const offer = v.current_admin_offer || 0;
  const retailLower = v.valuation?.retail_lower;
  const retailUpper = v.valuation?.retail_upper;
  const retailRec = v.valuation?.recommended || v.estimated_retail_value || 0;
  const retailGap = retailRec && expected ? retailRec - expected : null;
  const retailGapClass = retailGap === null
    ? "text-white/30"
    : retailGap > 0 ? "text-emerald-400" : retailGap < 0 ? "text-[#FF1E1E]" : "text-white";

  const assignedStaff = v.assigned_to_user_id ? (staffUsers || []).find(u => u.id === v.assigned_to_user_id) : null;
  const ownerLabel = assignedStaff?.name || assignedStaff?.phone || v.assigned_executive || "Unassigned";

  const [assignOpen, setAssignOpen] = useState(false);

  const assignToMe = () => {
    if (!currentUser?.id) return;
    onPatch({ assigned_to_user_id: currentUser.id });
    toast.success("Assigned to you");
    setAssignOpen(false);
  };
  const assignTo = (uid) => {
    onPatch({ assigned_to_user_id: uid || "" });
    toast.success(uid ? "Assigned" : "Unassigned");
    setAssignOpen(false);
  };

  return (
    <section
      className="bg-gradient-to-br from-[#121212] to-[#0A0A0A] border border-[#2A2A2A] p-4 sm:p-5"
      data-testid={`acquisition-hero-${v.id}`}
    >
      {/* Vehicle name + specs */}
      <div className="mb-4">
        <div className="font-display text-lg sm:text-2xl text-white leading-tight">
          {v.make} <span className="text-white/85">{v.model}</span>
          {v.variant && <span className="block sm:inline text-white/45 text-sm sm:ml-2">· {v.variant}</span>}
        </div>
        <div className="text-[11px] font-mono-data text-white/55 tracking-wider mt-1">
          {v.year || "—"} · {(v.km_driven || 0).toLocaleString()} km · {v.fuel_type || "—"} · {v.transmission || "—"}
          {v.registration_number ? <span className="text-white/40"> · {v.registration_number}</span> : null}
        </div>
      </div>

      {/* Key acquisition decision metrics — 4 tiles on mobile (2×2), 4 wide on sm+ */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4">
        <Tile k="Current Expected" value={fmtINR(expected)} testid={`hero-expected-${v.id}`} />
        <Tile k="Offer Given" value={offer ? fmtINR(offer) : "—"} valueClass="text-[#FF1E1E]" testid={`hero-offer-${v.id}`} />
        <Tile
          k="Retail Range"
          value={retailLower && retailUpper ? `${fmtINR(retailLower)} – ${fmtINR(retailUpper)}` : "—"}
          smallValue
          testid={`hero-retail-${v.id}`}
        />
        <Tile
          k="Retail Gap"
          value={retailGap === null ? "—" : `${retailGap >= 0 ? "+" : ""}${fmtINR(retailGap)}`}
          valueClass={retailGapClass}
          testid={`hero-retail-gap-${v.id}`}
        />
      </div>

      {/* Liquidity · Stage · Assigned strip */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4 text-[10px] sm:text-[11px] font-mono-data tracking-wider uppercase">
        <div className="bg-[#0A0A0A] border-l-2 border-white/15 px-2 py-1.5">
          <div className="text-white/40 text-[9px]">Liquidity</div>
          <div className="text-white text-sm font-display normal-case">{v.liquidity_score ? `${v.liquidity_score}/10` : "—"}</div>
        </div>
        <div className="bg-[#0A0A0A] border-l-2 border-[#FF1E1E] px-2 py-1.5">
          <div className="text-white/40 text-[9px]">Stage</div>
          <div className="text-[#FF1E1E] text-xs font-display normal-case truncate">{(v.acquisition_stage || "new_lead").replace(/_/g, " ")}</div>
        </div>
        <div className="bg-[#0A0A0A] border-l-2 border-white/15 px-2 py-1.5 min-w-0">
          <div className="text-white/40 text-[9px]">Assigned</div>
          <div className="text-white text-xs font-display normal-case truncate">{ownerLabel}</div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-3 gap-2">
        <a
          href={callHref || undefined}
          aria-disabled={!callHref}
          data-testid={`hero-call-${v.id}`}
          className={`btn-primary text-[11px] py-2 px-2 inline-flex items-center justify-center gap-1.5 ${callHref ? "" : "opacity-30 pointer-events-none"}`}
        >
          <Phone size={12} weight="bold" /> Call
        </a>
        <a
          href={waHref || undefined}
          target="_blank"
          rel="noopener noreferrer"
          aria-disabled={!waHref}
          data-testid={`hero-wa-${v.id}`}
          className={`text-[11px] py-2 px-2 inline-flex items-center justify-center gap-1.5 font-display tracking-widest uppercase border border-[#25D366]/60 text-[#25D366] hover:bg-[#25D366]/10 ${waHref ? "" : "opacity-30 pointer-events-none"}`}
        >
          <WhatsappLogo size={12} weight="bold" /> WhatsApp
        </a>
        <div className="relative">
          <button
            onClick={() => setAssignOpen(o => !o)}
            data-testid={`hero-assign-${v.id}`}
            className="btn-secondary text-[11px] py-2 px-2 w-full inline-flex items-center justify-center gap-1.5"
          >
            <Check size={12} weight="bold" /> Assign
          </button>
          {assignOpen && (
            <div
              className="absolute right-0 top-full mt-1 z-40 w-56 bg-[#0E0E0E] border border-[#2A2A2A] shadow-xl py-1"
              data-testid={`hero-assign-menu-${v.id}`}
            >
              {currentUser?.id && v.assigned_to_user_id !== currentUser.id && (
                <button onClick={assignToMe} className="block w-full text-left px-3 py-1.5 text-xs text-white hover:bg-[#1A1A1A]">
                  Assign to me
                </button>
              )}
              {(staffUsers || []).filter(u => u.status !== "inactive").map(u => (
                <button key={u.id} onClick={() => assignTo(u.id)} className="block w-full text-left px-3 py-1.5 text-xs text-white/85 hover:bg-[#1A1A1A] truncate">
                  {u.name || u.phone}
                </button>
              ))}
              {v.assigned_to_user_id && (
                <button onClick={() => assignTo("")} className="block w-full text-left px-3 py-1.5 text-xs text-white/55 hover:bg-[#1A1A1A] border-t border-[#2A2A2A]">
                  Unassign
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Tile({ k, value, valueClass, smallValue, testid }) {
  return (
    <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-2.5">
      <div className="text-[9px] uppercase tracking-widest text-white/40">{k}</div>
      <div
        className={`${smallValue ? "text-[11px] font-mono-data" : "text-base sm:text-lg font-display"} ${valueClass || "text-white"} mt-0.5 truncate`}
        data-testid={testid}
      >
        {value}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// TabBar — Details / Negotiation (mobile only) / Media / History
// Tabs adapt: on lg+ the Negotiation tab is hidden because the right
// rail keeps that data permanently visible.
// -------------------------------------------------------------------
function TabBar({ v, active, onSelect }) {
  const TABS = [
    { k: "details",     label: "Details" },
    { k: "negotiation", label: "Negotiation", mobileOnly: true },
    { k: "media",       label: "Media" },
    { k: "buyers",      label: "Buyers" },
    { k: "history",     label: "History" },
  ];
  return (
    <div className="flex gap-0 border-b border-[#1F1F1F] -mx-3 sm:-mx-5 px-3 sm:px-5 overflow-x-auto" data-testid={`lead-tabs-${v.id}`}>
      {TABS.map(t => (
        <button
          key={t.k}
          onClick={() => onSelect(t.k)}
          data-testid={`lead-tab-${t.k}-${v.id}`}
          className={`text-[11px] font-display tracking-widest uppercase py-2.5 px-3 sm:px-4 border-b-2 transition-colors whitespace-nowrap ${
            active === t.k
              ? "border-[#FF1E1E] text-white"
              : "border-transparent text-white/45 hover:text-white/75"
          } ${t.mobileOnly ? "lg:hidden" : ""}`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// -------------------------------------------------------------------
// MediaSection — Photos + (future) Documents + Inspection Images.
// Reuses the existing PhotoGallery; placeholders for the other categories
// so the structure is ready when those data sources land.
// -------------------------------------------------------------------
function MediaSection({ v }) {
  const photoCount = (v.photos || []).length;
  const docCount = (v.documents || []).length;
  const inspectionCount = (v.inspection_photos || []).length;
  return (
    <div className="space-y-5" data-testid={`media-tab-${v.id}`}>
      <Section title={`Photos · ${photoCount}`}>
        {photoCount === 0
          ? <div className="text-xs text-white/40">No photos uploaded.</div>
          : <PhotoGallery vehicle={v} />}
      </Section>
      <Section title={`Documents · ${docCount}`}>
        {docCount === 0
          ? <div className="text-xs text-white/40">No documents uploaded. RC, insurance, service records will appear here.</div>
          : <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(v.documents || []).map((d, i) => (
                <a key={i} href={d.url} target="_blank" rel="noopener noreferrer" className="bg-[#0E0E0E] border border-[#2A2A2A] p-2 text-xs text-white/75 truncate hover:border-[#FF1E1E]">
                  {d.label || `Document ${i + 1}`}
                </a>
              ))}
            </div>}
      </Section>
      <Section title={`Inspection Images · ${inspectionCount}`}>
        {inspectionCount === 0
          ? <div className="text-xs text-white/40">No inspection imagery yet.</div>
          : <div className="grid grid-cols-3 sm:grid-cols-6 gap-1">
              {(v.inspection_photos || []).map((p, i) => (
                <img key={i} src={resolvePhotoUrl(p)} alt="" loading="lazy" className="w-full aspect-square object-cover bg-[#0A0A0A]" />
              ))}
            </div>}
      </Section>
    </div>
  );
}

// -------------------------------------------------------------------
// StickyActionBar — fixed-to-viewport-bottom on mobile while the lead
// expansion is in view. Provides Call / WhatsApp / Offer / Note.
// On lg+ it stays sticky too, but hides because the right rail already
// has the Offer input and the action bar would be redundant.
// -------------------------------------------------------------------
function StickyActionBar({ v, callHref, waHref, onOffer, onNote }) {
  return (
    <div
      className="sticky bottom-0 z-30 bg-[#0A0A0A]/95 backdrop-blur-sm border-t border-white/10 grid grid-cols-4 gap-px lg:hidden"
      data-testid={`sticky-actions-${v.id}`}
    >
      <a
        href={callHref || undefined}
        aria-disabled={!callHref}
        data-testid={`bar-call-${v.id}`}
        className={`flex flex-col items-center justify-center py-2.5 bg-[#0E0E0E] hover:bg-[#1A1A1A] ${callHref ? "" : "opacity-30 pointer-events-none"}`}
      >
        <Phone size={16} weight="bold" className="text-[#FF1E1E]" />
        <span className="text-[9px] tracking-widest font-display uppercase text-white/85 mt-0.5">Call</span>
      </a>
      <a
        href={waHref || undefined}
        target="_blank"
        rel="noopener noreferrer"
        aria-disabled={!waHref}
        data-testid={`bar-wa-${v.id}`}
        className={`flex flex-col items-center justify-center py-2.5 bg-[#0E0E0E] hover:bg-[#1A1A1A] ${waHref ? "" : "opacity-30 pointer-events-none"}`}
      >
        <WhatsappLogo size={16} weight="bold" className="text-[#25D366]" />
        <span className="text-[9px] tracking-widest font-display uppercase text-white/85 mt-0.5">WhatsApp</span>
      </a>
      <button
        onClick={onOffer}
        data-testid={`bar-offer-${v.id}`}
        className="flex flex-col items-center justify-center py-2.5 bg-[#0E0E0E] hover:bg-[#1A1A1A]"
      >
        <PaperPlaneTilt size={16} weight="bold" className="text-white" />
        <span className="text-[9px] tracking-widest font-display uppercase text-white/85 mt-0.5">Offer</span>
      </button>
      <button
        onClick={onNote}
        data-testid={`bar-note-${v.id}`}
        className="flex flex-col items-center justify-center py-2.5 bg-[#0E0E0E] hover:bg-[#1A1A1A]"
      >
        <NotePencil size={16} weight="bold" className="text-white" />
        <span className="text-[9px] tracking-widest font-display uppercase text-white/85 mt-0.5">Note</span>
      </button>
    </div>
  );
}



// ===================================================================
// Section 1 — Vehicle details
// ===================================================================
function VehicleSection({ v }) {
  const rows = [
    ["Brand", v.make], ["Model", v.model], ["Variant", v.variant || "—"],
    ["Registration", v.registration_number || "—"],
    ["Fuel", v.fuel_type], ["Transmission", v.transmission],
    ["Year", v.year], ["KM Driven", `${(v.km_driven || 0).toLocaleString()} km`],
    ["Owners", v.owners], ["Hypothecation", v.hypothecation ? "Yes" : "No"],
    ["Accident", v.accident_history || "—"], ["Service History", v.service_history || "—"],
    ["Condition Score", `${v.condition_score ?? 0}%`],
  ];
  return (
    <Section title="Vehicle Details">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1.5 font-mono-data text-xs">
        {rows.map(([k, val]) => (
          <div key={k} className="flex justify-between border-b border-[#1A1A1A] py-1">
            <span className="text-white/40 uppercase tracking-widest text-[10px]">{k}</span>
            <span className="text-white text-right truncate ml-2">{val}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ===================================================================
// Section 3 — Seller (compact: copy + whatsapp icons only)
// ===================================================================
function SellerSection({ v, phoneE164, phoneForWA, onPatch }) {
  const copy = async (val, label) => {
    try { await navigator.clipboard.writeText(val); toast.success(`${label} copied`); }
    catch { toast.error("Copy blocked"); }
  };
  return (
    <Section title="Seller">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1.5 font-mono-data text-xs">
        <KV k="Name" v={v.seller_name || "—"} />
        <KV k="Mobile" v={
          <span className="inline-flex items-center gap-1.5 flex-wrap">
            <span>{v.seller_phone || "—"}</span>
            {v.seller_phone && (
              <PhoneQuickActions phone={v.seller_phone} compact testidPrefix={`seller-phoneqa-${v.id}`}
                                 prefill={`Hi ${v.seller_name || "there"}, this is Q Drives following up on your ${v.make} ${v.model}.`} />
            )}
          </span>
        } />
        <KV k="Source" v={
          <select
            data-testid={`source-select-${v.id}`}
            value={v.source || "Website"}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onPatch({ lead_source: e.target.value })}
            className="bg-[#0A0A0A] border border-[#2A2A2A] text-white text-[11px] py-0.5 px-1 font-mono-data tracking-wider focus:border-[#FF1E1E] outline-none"
          >
            {LEAD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        } />
        <KV k="Created" v={timeAgo(v.created_at) + " ago"} />
      </div>
    </Section>
  );
}

// ===================================================================
// NegotiationWorkspace — Redesigned Pricing & Negotiation workspace.
//
// Layout (per spec):
//   Section 1 — NEGOTIATION CORE (always expanded, top of viewport)
//     · Seller Ask · Current Expected · Last Offer · New Offer + Note + SEND
//   Section 2 — Acquisition Strategy (collapsible, internal only)
//     · Internal Valuation · Target Buy · Max Buy · Liquidity · Ready To Buy
//   Section 3 — Retail Strategy (collapsible)
//     · Range · Recommended · Confidence · AI Analysis (nested expander)
//   Section 4 — Profitability (collapsible)
//     · Auto-computed Gross / Net / ROI
//
// SuggestedBuyers is no longer rendered here — it lives in the Buyers tab.
// ===================================================================
function NegotiationWorkspace({ v, onPatch, onRefresh }) {
  return (
    <div className="space-y-3" data-testid={`negotiation-workspace-${v.id}`}>
      <NegotiationCore v={v} onPatch={onPatch} />
      <CollapsibleCard title="Pricing / Acquisition Inputs" defaultOpen testid={`collapse-ai-pricing-${v.id}`}>
        <PricingInputsStub />
      </CollapsibleCard>
      <CollapsibleCard title="Acquisition Strategy" testid={`collapse-acquisition-${v.id}`}>
        <div className="space-y-4">
          <ProfitSummary v={v} />
          <ProfitabilitySection v={v} onPatch={onPatch} onRefresh={onRefresh} />
        </div>
      </CollapsibleCard>
    </div>
  );
}

// CollapsibleCard — lightweight disclosure wrapper.
function CollapsibleCard({ title, children, defaultOpen = false, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[#1F1F1F] bg-[#0A0A0A]" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-[#101010] transition-colors"
        data-testid={`${testid}-toggle`}
      >
        <span className="text-[11px] tracking-widest uppercase font-display text-white/75">{title}</span>
        <span className="text-white/45 text-[10px]">{open ? "▾" : "▸"}</span>
      </button>
      {open && <div className="px-3 pb-3 pt-1 border-t border-[#1F1F1F]">{children}</div>}
    </div>
  );
}

// NegotiationCore — the always-visible compact panel. Optimized for fast
// "phone in hand" negotiations: see asks/offers, type new offer, send.
function NegotiationCore({ v, onPatch }) {
  const [offer, setOffer] = useState(v.current_admin_offer || "");
  const [note, setNote] = useState("");
  const [expectedEdit, setExpectedEdit] = useState(v.current_expected_price ?? v.expected_price ?? "");
  const [editingExpected, setEditingExpected] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setOffer(v.current_admin_offer || "");
    setExpectedEdit(v.current_expected_price ?? v.expected_price ?? "");
  }, [v.id, v.current_admin_offer, v.current_expected_price, v.expected_price]);

  const sellerAsk = v.initial_ask_price ?? v.expected_price ?? 0;
  const currentExpected = v.current_expected_price ?? v.expected_price ?? 0;
  const lastOfferAmount = v.last_dealership_offer_event?.amount || v.current_admin_offer || 0;

  // Auto-save current expected price on blur (no Save button).
  const saveExpectedOnBlur = async () => {
    const val = parseInt(expectedEdit, 10) || 0;
    if (val > 0 && val !== currentExpected) {
      await onPatch({ current_expected_price: val });
      toast.success(`Current expected updated → ${fmtINR(val)}`);
    }
    setEditingExpected(false);
  };

  const sendOffer = async (kind = "initial") => {
    const amount = parseInt(offer, 10) || 0;
    if (amount <= 0) return toast.error("Enter a valid offer");
    setSending(true);
    try {
      const res = await api.post(`/admin/offers/${v.id}`, {
        amount,
        send_whatsapp: true,
        template_kind: kind,
      });
      const wa = res?.data?.whatsapp;
      if (wa?.status === "failed") {
        toast.error(`Offer recorded in PWA but WhatsApp failed: ${wa.failure_reason || "unknown"}`);
      } else if (wa?.message_id) {
        toast.success("WhatsApp purchase offer sent successfully.");
      } else {
        toast.success(`Offer ${fmtINR(amount)} sent`);
      }
      setNote("");
      try { onPatch?.({}); } catch { /* noop */ }
    } catch (e) {
      toast.error("Offer failed: " + (e?.response?.data?.detail || e.message));
    } finally { setSending(false); }
  };

  return (
    <div className="bg-gradient-to-br from-[#121212] to-[#0A0A0A] border border-[#2A2A2A] p-3 sm:p-4" data-testid={`negotiation-core-${v.id}`}>
      {/* Top: Seller Ask · Current Expected · Last Offer (compact 3-up) */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <CompactKV k="Seller Ask" v={fmtINR(sellerAsk)} testid={`core-seller-ask-${v.id}`} />
        <button
          type="button"
          onClick={() => setEditingExpected(true)}
          className="text-left bg-[#0E0E0E] border border-[#1F1F1F] px-2 py-1.5 hover:border-[#FF1E1E]/50"
          title="Click to edit"
          data-testid={`core-expected-edit-${v.id}`}
        >
          <div className="text-[9px] uppercase tracking-widest text-white/40">Current Expected</div>
          {editingExpected ? (
            <input
              autoFocus
              value={expectedEdit}
              onChange={(e) => setExpectedEdit(e.target.value.replace(/[^\d]/g, ""))}
              onBlur={saveExpectedOnBlur}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              className="w-full bg-transparent text-white text-sm font-display focus:outline-none"
              inputMode="numeric"
              data-testid={`core-expected-input-${v.id}`}
            />
          ) : (
            <div className="text-white text-sm font-display truncate" data-testid={`core-expected-${v.id}`}>{fmtINR(currentExpected)}</div>
          )}
        </button>
        <CompactKV k="Last Offer" v={lastOfferAmount ? fmtINR(lastOfferAmount) : "—"} valueClass="text-[#FF1E1E]" testid={`core-last-offer-${v.id}`} />
      </div>

      {/* New Offer + Note + Send buttons */}
      <div className="space-y-2">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-white/40 mb-1">New Offer</div>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={offer}
            onChange={(e) => setOffer(e.target.value.replace(/[^\d]/g, ""))}
            placeholder="₹"
            data-testid={`core-offer-${v.id}`}
            className="qd-input-block w-full text-base py-2 font-display"
          />
        </div>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note (optional)"
          data-testid={`core-note-${v.id}`}
          className="qd-input-block w-full text-xs py-1.5"
        />
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => sendOffer("initial")}
            disabled={sending}
            data-testid={`core-send-offer-${v.id}`}
            className="btn-primary text-[11px] py-2 px-2 inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
          >
            <WhatsappLogo size={12} weight="bold" /> Send Offer
          </button>
          <button
            onClick={() => sendOffer("updated")}
            disabled={sending}
            data-testid={`core-send-updated-${v.id}`}
            className="btn-secondary text-[11px] py-2 px-2 inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
          >
            <ArrowClockwise size={12} weight="bold" /> Updated Offer
          </button>
        </div>
      </div>
    </div>
  );
}

function CompactKV({ k, v, valueClass, testid }) {
  return (
    <div className="bg-[#0E0E0E] border border-[#1F1F1F] px-2 py-1.5" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest text-white/40">{k}</div>
      <div className={`text-sm font-display truncate ${valueClass || "text-white"}`}>{v}</div>
    </div>
  );
}

// ProfitSummary — auto-computed; no manual fields, no Save button.
function ProfitSummary({ v }) {
  const retail = v.estimated_retail_value || v.valuation?.recommended || 0;
  const recon  = v.expected_recon_cost || v.expected_reconditioning_cost || 0;
  const offer  = v.current_admin_offer || 0;
  const grossProfit = retail && offer ? retail - offer : 0;
  const netProfit   = grossProfit ? grossProfit - recon : 0;
  const roi = offer ? (netProfit / offer) * 100 : 0;
  // Days in pipeline (proxy for "Days To Sell" — actual sell-through time once won).
  const daysInPipeline = v.created_at
    ? Math.max(0, Math.floor((Date.now() - new Date(v.created_at).getTime()) / 86400000))
    : null;
  // Color the aging KPI: <=7d white, 8-21d amber, >21d red.
  const daysClass = daysInPipeline === null ? "text-white/30"
    : daysInPipeline <= 7 ? "text-white"
    : daysInPipeline <= 21 ? "text-amber-400"
    : "text-[#FF1E1E]";
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono-data" data-testid={`profit-summary-${v.id}`}>
      <CompactKV k="Gross Profit" v={hasInputsFmt(retail, offer, grossProfit)} valueClass={grossProfit >= 0 ? "text-emerald-400" : "text-[#FF1E1E]"} testid={`profit-gross-${v.id}`} />
      <CompactKV k="Net Profit"   v={hasInputsFmt(retail, offer, netProfit)}   valueClass={netProfit   >= 0 ? "text-emerald-400" : "text-[#FF1E1E]"} testid={`profit-net-${v.id}`} />
      <CompactKV k="ROI" v={offer && retail ? `${roi.toFixed(1)}%` : "—"} valueClass={roi >= 0 ? "text-emerald-400" : "text-[#FF1E1E]"} testid={`profit-roi-${v.id}`} />
      <CompactKV k="Days In Pipeline" v={daysInPipeline === null ? "—" : `${daysInPipeline}d`} valueClass={daysClass} testid={`profit-days-${v.id}`} />
    </div>
  );
}

// Small helper: only show a profit figure once we have both retail value + offer.
function hasInputsFmt(retail, offer, value) {
  return retail && offer ? fmtINR(value) : "—";
}


// ===================================================================
// Section 4 — Negotiation (compact horizontal)
// ===================================================================
function NegotiationSection({ v, onPatch }) {
  const [offer, setOffer] = useState(v.current_admin_offer || "");
  const [valuation, setValuation] = useState(v.internal_valuation || "");
  // Current Expected Price defaults to initial ask. Editable as negotiations progress.
  const [expected, setExpected] = useState(v.current_expected_price ?? v.expected_price ?? "");
  const [note, setNote] = useState("");
  useEffect(() => {
    setOffer(v.current_admin_offer || "");
    setValuation(v.internal_valuation || "");
    setExpected(v.current_expected_price ?? v.expected_price ?? "");
  }, [v.id, v.current_admin_offer, v.internal_valuation, v.current_expected_price, v.expected_price]);

  const initialAsk = v.initial_ask_price ?? v.expected_price ?? 0;
  const currentExpected = v.current_expected_price ?? v.expected_price ?? 0;
  // Price movement helpers
  const expectedDelta = currentExpected && initialAsk ? currentExpected - initialAsk : 0;
  const expectedDeltaPct = initialAsk ? (expectedDelta / initialAsk) * 100 : 0;

  const sendOffer = async (kind = "initial") => {
    const amount = parseInt(offer, 10) || 0;
    if (amount <= 0) return toast.error("Enter a valid offer");
    try {
      // POST /admin/offers/{vid} returns {pwa, whatsapp} — additive flow.
      const res = await api.post(`/admin/offers/${v.id}`, {
        amount,
        send_whatsapp: true,
        template_kind: kind,
      });
      const wa = res?.data?.whatsapp;
      if (wa?.status === "failed") {
        toast.error(`Offer recorded in PWA but WhatsApp failed: ${wa.failure_reason || "unknown"}`);
      } else if (wa?.message_id) {
        toast.success("WhatsApp purchase offer sent successfully.");
      } else {
        toast.success(`Offer ₹${amount.toLocaleString()} sent (PWA only)`);
      }
      setNote("");
      // Bubble up to refresh the lead so current_admin_offer + history reflect.
      try { onPatch?.({}); } catch { /* noop */ }
    } catch (e) {
      toast.error("Offer failed: " + (e?.response?.data?.detail || e.message));
    }
  };
  const saveValuation = async () => {
    const val = parseInt(valuation, 10) || 0;
    await onPatch({ internal_valuation: val });
    toast.success("Internal valuation saved");
  };
  const saveExpected = async () => {
    const val = parseInt(expected, 10) || 0;
    if (val <= 0) return toast.error("Enter a valid expected price");
    await onPatch({ current_expected_price: val });
    toast.success("Current expected price updated");
  };

  const lastOffer = v.last_dealership_offer_event;

  return (
    <Section title="Pricing & Negotiation">
      {/* Row 1 — the 3 mandated price fields */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <KVStack k="Initial Ask Price">
          <div className="font-display text-base text-white">{fmtINR(initialAsk)}</div>
          <div className="text-[10px] text-white/40">Captured at lead creation · immutable</div>
        </KVStack>
        <KVStack k="Current Expected Price">
          <div className="flex gap-1">
            <input type="text" inputMode="numeric" pattern="[0-9]*" value={expected}
              onChange={(e) => setExpected(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="₹" data-testid={`expected-${v.id}`}
              className="qd-input-block flex-1 text-sm py-1.5"/>
            <button onClick={saveExpected} data-testid={`expected-save-${v.id}`} className="btn-secondary text-[10px] py-1.5 px-2">Save</button>
          </div>
          {expectedDelta !== 0 && initialAsk > 0 && (
            <div
              className={`text-[10px] font-mono-data mt-0.5 ${expectedDelta < 0 ? "text-emerald-400" : "text-[#FF1E1E]"}`}
              data-testid={`expected-delta-${v.id}`}
            >
              {expectedDelta < 0 ? "↓" : "↑"} ₹{Math.abs(expectedDelta).toLocaleString()} ({expectedDeltaPct.toFixed(1)}%) vs initial
            </div>
          )}
        </KVStack>
        <KVStack k="Offer Given">
          <div className="font-display text-base text-[#FF1E1E]">{v.current_admin_offer ? fmtINR(v.current_admin_offer) : "—"}</div>
          {lastOffer && <div className="text-[10px] text-white/40">{timeAgo(lastOffer.at)} ago</div>}
        </KVStack>
      </div>

      {/* Row 2 — internal valuation + new offer */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
        <KVStack k="Internal Valuation">
          <div className="flex gap-1">
            <input type="text" inputMode="numeric" pattern="[0-9]*" value={valuation}
              onChange={(e) => setValuation(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="0" data-testid={`val-${v.id}`}
              className="qd-input-block flex-1 text-sm py-1.5"/>
            <button onClick={saveValuation} data-testid={`val-save-${v.id}`} className="btn-secondary text-[10px] py-1.5 px-2">Save</button>
          </div>
        </KVStack>
        <KVStack k="Send New Offer">
          <div className="flex gap-1">
            <input type="text" inputMode="numeric" pattern="[0-9]*" value={offer}
              onChange={(e) => setOffer(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="₹" data-testid={`offer-${v.id}`}
              className="qd-input-block flex-1 text-sm py-1.5"/>
          </div>
          <div className="flex gap-1.5 mt-1.5">
            <button
              onClick={() => sendOffer("initial")}
              data-testid={`send-offer-${v.id}`}
              className="btn-primary text-[10px] py-1.5 px-2 inline-flex items-center gap-1 flex-1 justify-center"
              title="Sends PWA notification + WhatsApp purchase_offer template"
            >
              <WhatsappLogo size={11} weight="bold" />Send Offer
            </button>
            <button
              onClick={() => sendOffer("updated")}
              data-testid={`send-updated-offer-${v.id}`}
              className="btn-secondary text-[10px] py-1.5 px-2 inline-flex items-center gap-1 flex-1 justify-center"
              title="Sends PWA notification + WhatsApp updated_purchase_offer (negotiation) template"
            >
              <ArrowClockwise size={11} weight="bold" />Updated
            </button>
          </div>
        </KVStack>
      </div>

      <div className="mt-3">
        <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Optional negotiation note (sent with the offer)…"
          data-testid={`offer-note-${v.id}`}
          className="qd-input-block text-xs py-1.5"/>
      </div>
    </Section>
  );
}


// ===================================================================
// Section 4a — Retail Valuation (provider-agnostic display)
// Renders the canonical valuation shape regardless of source (AI, OBV, etc).
// ===================================================================
// Stable confidence badge component — defined outside ValuationSection so React
// doesn't remount it every render.
function ConfidenceBadge({ score }) {
  if (score == null) return null;
  const cls = score >= 80 ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40"
            : score >= 65 ? "bg-amber-500/15 text-amber-400 border-amber-500/40"
            : "bg-rose-500/15 text-rose-400 border-rose-500/40";
  const label = score >= 80 ? "Strong" : score >= 65 ? "Average" : "Weak";
  return (
    <span className={`px-2 py-0.5 text-[10px] font-mono-data tracking-widest uppercase border ${cls}`}>
      {score}% · {label}
    </span>
  );
}

// PricingInputsStub — replaces the retired Claude AI Pricing Engine card body.
// All pricing fields (Estimated Retail Value, Target Buy Price, Days in Pipeline,
// Max Buy Price, Reconditioning Cost, Liquidity Score) now live inside the
// Acquisition Strategy section below and are edited manually.
function PricingInputsStub() {
  return (
    <div
      data-testid="pricing-inputs-stub"
      className="text-sm text-white/55 leading-relaxed"
    >
      <p className="text-white/70 font-medium mb-1.5">Pricing fields are now manual.</p>
      <p>
        Edit <span className="text-white/85">Estimated Retail Value</span>,{" "}
        <span className="text-white/85">Target Buy Price</span>, and{" "}
        <span className="text-white/85">Days in Pipeline</span> in the{" "}
        <span className="text-[#FF1E1E]">Acquisition Strategy</span> section
        below. Gross / Net spread and ROI continue to auto-calculate.
      </p>
    </div>
  );
}

function ValuationSection({ v, onRefresh }) {  const val = v.valuation || null;
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // "retail_lower" | "retail_upper" | "recommended"
  const [draft, setDraft] = useState("");

  const regen = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/vehicles/${v.id}/valuation/regenerate`);
      toast.success("Valuation regenerated");
      onRefresh?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Regenerate failed";
      toast.error(detail);
    } finally { setBusy(false); }
  };

  const saveField = async (field) => {
    const n = parseInt(draft, 10) || 0;
    if (n < 0) { setEditing(null); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/vehicles/${v.id}/valuation`, { [field]: n });
      toast.success("Valuation updated");
      setEditing(null); setDraft("");
      onRefresh?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Update failed";
      toast.error(detail);
    } finally { setBusy(false); }
  };


  return (
    <Section title="AI Pricing Engine">
      {!val ? (
        <div className="flex items-center justify-between gap-3 py-2">
          <div className="text-[12px] text-white/50">
            No valuation yet. {v.make && v.model ? "Generate one now." : "Need brand + model to value this vehicle."}
          </div>
          <button
            data-testid={`regen-val-${v.id}`}
            onClick={regen}
            disabled={busy || !v.make || !v.model}
            className="btn-secondary text-[10px] py-1.5 px-3 disabled:opacity-30"
          >
            {busy ? "Generating…" : "Generate"}
          </button>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2 text-[11px] font-mono-data text-white/55">
              <span>Source: <span className="text-white">{val.source || "AI Estimate"}</span></span>
              <span className="text-white/25">·</span>
              <span>Generated: <span className="text-white">{val.generated_at ? new Date(val.generated_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}</span></span>
              {val.manually_edited && (
                <>
                  <span className="text-white/25">·</span>
                  <span className="text-amber-400">manually edited</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <ConfidenceBadge score={val.confidence} />
              <button
                data-testid={`regen-val-${v.id}`}
                onClick={regen}
                disabled={busy}
                className="btn-secondary text-[10px] py-1.5 px-2.5 flex items-center gap-1 disabled:opacity-30"
              >
                <ArrowClockwise size={11} weight="bold" />
                {busy ? "…" : "Regenerate"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { k: "retail_lower",  label: "Retail Lower",       testid: "val-lower" },
              { k: "retail_upper",  label: "Retail Upper",       testid: "val-upper" },
              { k: "recommended",   label: "Recommended Retail", testid: "val-rec", emphasize: true },
            ].map(({ k, label, testid, emphasize }) => (
              <div key={k} className="border border-[#1F1F1F] bg-[#0A0A0A] p-3">
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{label}</div>
                {editing === k ? (
                  <input
                    autoFocus
                    type="text" inputMode="numeric" pattern="[0-9]*"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value.replace(/[^\d]/g, ""))}
                    onBlur={() => saveField(k)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveField(k); if (e.key === "Escape") setEditing(null); }}
                    className={`bg-transparent border-b border-[#FF1E1E] outline-none font-display text-xl py-0.5 w-full ${emphasize ? "text-[#FF1E1E]" : "text-white"}`}
                    data-testid={`${testid}-input-${v.id}`}
                  />
                ) : (
                  <button
                    data-testid={`${testid}-${v.id}`}
                    onClick={() => { setEditing(k); setDraft(String(val[k] || "")); }}
                    className={`font-display text-xl text-left w-full ${emphasize ? "text-[#FF1E1E]" : "text-white"}`}
                  >
                    {fmtINR(val[k])}
                  </button>
                )}
                <div className="text-[10px] text-white/40 mt-1 font-mono-data">tap to edit</div>
              </div>
            ))}
          </div>

          {val.reasoning && (
            <div className="mt-3 text-[11px] text-white/50 leading-relaxed italic">
              &ldquo;{val.reasoning}&rdquo;
            </div>
          )}
        </>
      )}
    </Section>
  );
}


// ===================================================================
// Section 4b — Profitability (Retail value, recon cost, gross/net spread)
// ===================================================================
function ProfitabilitySection({ v, onPatch, onRefresh }) {
  const [retail, setRetail] = useState(v.estimated_retail_value || "");
  const [recon, setRecon]   = useState(v.expected_reconditioning_cost || "");
  const [target, setTarget] = useState(v.target_buy_price || "");
  const [maxBuy, setMaxBuy] = useState(v.max_buy_price || "");
  const [days, setDays]     = useState(v.days_in_pipeline || "");
  const [liqBusy, setLiqBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);

  useEffect(() => {
    setRetail(v.estimated_retail_value || "");
    setRecon(v.expected_reconditioning_cost || "");
    setTarget(v.target_buy_price || "");
    setMaxBuy(v.max_buy_price || "");
    setDays(v.days_in_pipeline || "");
  }, [v.id, v.estimated_retail_value, v.expected_reconditioning_cost, v.target_buy_price, v.max_buy_price, v.days_in_pipeline]);

  const ready = !!v.ready_to_buy;

  const offer = v.current_admin_offer || 0;
  const retailNum = parseInt(retail, 10) || 0;
  const reconNum  = parseInt(recon, 10) || 0;
  const hasInputs = retailNum > 0 && offer > 0;
  const grossSpread = hasInputs ? retailNum - offer : 0;
  const netSpread   = hasInputs ? grossSpread - reconNum : 0;
  const netMarginPct = retailNum > 0 ? (netSpread / retailNum) * 100 : 0;

  const saveRetail = async () => {
    const val = parseInt(retail, 10) || 0;
    if (val < 0) return toast.error("Enter a valid retail value");
    await onPatch({ estimated_retail_value: val });
    toast.success("Estimated retail value updated");
  };
  const saveRecon = async () => {
    const val = parseInt(recon, 10) || 0;
    if (val < 0) return toast.error("Enter a valid reconditioning cost");
    await onPatch({ expected_reconditioning_cost: val });
    toast.success("Reconditioning cost updated");
  };

  const regenLiquidity = async () => {
    setLiqBusy(true);
    try {
      const res = await api.post(`/admin/vehicles/${v.id}/regenerate-liquidity`);
      const s = res?.data?.liquidity_score;
      toast.success(`Liquidity recomputed · ${typeof s === "number" ? s.toFixed(1) : s}/10`);
      onRefresh?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Regenerate failed";
      toast.error(detail);
    } finally { setLiqBusy(false); }
  };

  // Generate Valuation — calls backend OpenAI engine. Fills ERV, TBP, Days only.
  // On any OpenAI failure the backend returns 502 and existing values stay intact.
  const generateValuation = async () => {
    setGenBusy(true);
    try {
      await api.post(`/admin/vehicles/${v.id}/valuation/regenerate`);
      toast.success("Valuation generated · ERV · Target Buy · Days updated");
      onRefresh?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "OpenAI call failed";
      toast.error(detail);
    } finally { setGenBusy(false); }
  };

  const netClass = !hasInputs ? "text-white/30"
                  : netSpread > 0 ? "text-emerald-400"
                  : netSpread < 0 ? "text-[#FF1E1E]"
                  : "text-white";

  return (
    <Section title="Profitability">
      {/* Generate Valuation — backend calls OpenAI with vehicle specs and
          fills ERV, Target Buy, and Days only. Existing values are
          preserved if OpenAI fails. */}
      <div className="flex items-center justify-between gap-2 mb-3 pb-2 border-b border-white/8">
        <div className="text-[10px] text-white/40 leading-tight">
          One-click OpenAI valuation · Fills Estimated Retail Value · Target Buy Price · Days in Pipeline.
        </div>
        <button
          type="button"
          onClick={generateValuation}
          disabled={genBusy}
          data-testid={`generate-valuation-${v.id}`}
          title="Calls OpenAI with this vehicle's specs and auto-fills ERV, Target Buy, and Days in Pipeline."
          className="font-display tracking-widest uppercase text-[10px] py-1.5 px-3 border border-[#FF1E1E] text-[#FF1E1E] hover:bg-[#FF1E1E] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {genBusy ? "Generating…" : "Generate Valuation"}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <KVStack k="Estimated Retail Value">
          <div className="flex gap-1">
            <input type="text" inputMode="numeric" pattern="[0-9]*" value={retail}
              onChange={(e) => setRetail(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="₹" data-testid={`retail-${v.id}`}
              className="qd-input-block flex-1 text-sm py-1.5"/>
            <button onClick={saveRetail} data-testid={`retail-save-${v.id}`} className="btn-secondary text-[10px] py-1.5 px-2">Save</button>
          </div>
          <div className="text-[10px] text-white/40">Projected resale price</div>
        </KVStack>
        <KVStack k="Expected Reconditioning Cost">
          <div className="flex gap-1">
            <input type="text" inputMode="numeric" pattern="[0-9]*" value={recon}
              onChange={(e) => setRecon(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="₹" data-testid={`recon-${v.id}`}
              className="qd-input-block flex-1 text-sm py-1.5"/>
            <button onClick={saveRecon} data-testid={`recon-save-${v.id}`} className="btn-secondary text-[10px] py-1.5 px-2">Save</button>
          </div>
          <div className="text-[10px] text-white/40">Refurb + repair budget</div>
        </KVStack>
        <KVStack k="Liquidity Score">
          <div className="flex items-stretch gap-1">
            <div
              data-testid={`liquidity-display-${v.id}`}
              className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] text-white text-sm py-1.5 px-2 font-mono-data tracking-wider"
            >
              {v.liquidity_score != null
                ? `${Number(v.liquidity_score).toFixed(1)} / 10`
                : "—"}
            </div>
            <button
              type="button"
              onClick={regenLiquidity}
              disabled={liqBusy}
              data-testid={`liquidity-regen-${v.id}`}
              className="btn-secondary text-[10px] py-1.5 px-2 whitespace-nowrap"
              title="Recompute liquidity score from current vehicle fields"
            >
              {liqBusy ? "…" : "Regenerate"}
            </button>
          </div>
          <div className="text-[10px] text-white/40">Auto-scored 1–10 · 9+ hot · 5–7 average · &lt;5 avoid</div>
          <details className="mt-1">
            <summary className="text-[10px] text-white/40 cursor-pointer hover:text-white/60">Manual override</summary>
            <select
              data-testid={`liquidity-${v.id}`}
              value={v.liquidity_score ? Math.round(v.liquidity_score) : ""}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                if (n >= 1 && n <= 10) onPatch({ liquidity_score: n });
              }}
              className="mt-1 bg-[#0A0A0A] border border-[#2A2A2A] text-white text-sm py-1.5 px-2 font-mono-data tracking-wider focus:border-[#FF1E1E] outline-none w-full"
            >
              <option value="">— Pick whole number —</option>
              {[10,9,8,7,6,5,4,3,2,1].map((n) => (
                <option key={n} value={n}>{n}{n === 10 ? " · Extremely Liquid" : n === 1 ? " · Extremely Difficult" : ""}</option>
              ))}
            </select>
          </details>
        </KVStack>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="border border-[#1F1F1F] bg-[#0A0A0A] p-3">
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Expected Gross Spread</div>
          <div className={`font-display text-xl ${hasInputs ? (grossSpread >= 0 ? "text-white" : "text-[#FF1E1E]") : "text-white/30"}`} data-testid={`gross-spread-${v.id}`}>
            {hasInputs ? fmtINR(grossSpread) : "—"}
          </div>
          <div className="text-[10px] text-white/40 mt-1 font-mono-data">
            {hasInputs ? `${fmtINR(retailNum)} − ${fmtINR(offer)}` : "Set Retail Value + Offer to compute"}
          </div>
        </div>
        <div className="border border-[#1F1F1F] bg-[#0A0A0A] p-3">
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Expected Net Spread</div>
          <div className={`font-display text-xl ${netClass}`} data-testid={`net-spread-${v.id}`}>
            {hasInputs ? fmtINR(netSpread) : "—"}
          </div>
          <div className="text-[10px] text-white/40 mt-1 font-mono-data">
            {hasInputs
              ? `Gross − ${fmtINR(reconNum)} recon · ${netMarginPct.toFixed(1)}% margin`
              : "Includes reconditioning"}
          </div>
        </div>
      </div>

      {/* Phase 12 — acquisition decision row: Target Buy / Max Buy / Ready */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 pt-3 border-t border-white/8">
        <KVStack k="Target Buy Price">
          <div className="flex gap-1">
            <input type="text" inputMode="numeric" pattern="[0-9]*" value={target}
              onChange={(e) => setTarget(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="₹" data-testid={`target-${v.id}`}
              className="qd-input-block flex-1 text-sm py-1.5"/>
            <button onClick={async () => {
              const n = parseInt(target, 10) || 0;
              if (n < 0) return toast.error("Invalid value");
              await onPatch({ target_buy_price: n });
              toast.success("Target buy price saved");
            }} data-testid={`target-save-${v.id}`} className="btn-secondary text-[10px] py-1.5 px-2">Save</button>
          </div>
          <div className="text-[10px] text-white/40">Ideal acquisition price</div>
        </KVStack>
        <KVStack k="Maximum Buy Price">
          <div className="flex gap-1">
            <input type="text" inputMode="numeric" pattern="[0-9]*" value={maxBuy}
              onChange={(e) => setMaxBuy(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="₹" data-testid={`maxbuy-${v.id}`}
              className="qd-input-block flex-1 text-sm py-1.5"/>
            <button onClick={async () => {
              const n = parseInt(maxBuy, 10) || 0;
              if (n < 0) return toast.error("Invalid value");
              await onPatch({ max_buy_price: n });
              toast.success("Max buy price saved");
            }} data-testid={`maxbuy-save-${v.id}`} className="btn-secondary text-[10px] py-1.5 px-2">Save</button>
          </div>
          <div className="text-[10px] text-white/40">Walk-away ceiling</div>
        </KVStack>
        <KVStack k="Ready To Buy">
          <button
            data-testid={`ready-toggle-${v.id}`}
            onClick={() => onPatch({ ready_to_buy: !ready })}
            className={`text-[11px] font-display tracking-widest uppercase py-2 px-3 border ${
              ready
                ? "bg-emerald-500/15 border-emerald-500 text-emerald-400"
                : "bg-transparent border-white/20 text-white/55 hover:border-white/35"
            }`}
          >
            {ready ? "✓ READY TO BUY" : "Mark Ready To Buy"}
          </button>
          <div className="text-[10px] text-white/40">Manual management green-light</div>
        </KVStack>
      </div>

      {/* Days in Pipeline — populated by Generate Valuation; manual override available */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 pt-3 border-t border-white/8">
        <KVStack k="Days in Pipeline">
          <div className="flex gap-1">
            <input type="text" inputMode="numeric" pattern="[0-9]*" value={days}
              onChange={(e) => setDays(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="days" data-testid={`days-pipeline-${v.id}`}
              className="qd-input-block flex-1 text-sm py-1.5"/>
            <button onClick={async () => {
              const n = parseInt(days, 10) || 0;
              if (n < 0) return toast.error("Invalid value");
              await onPatch({ days_in_pipeline: n });
              toast.success("Days in pipeline saved");
            }} data-testid={`days-pipeline-save-${v.id}`} className="btn-secondary text-[10px] py-1.5 px-2">Save</button>
          </div>
          <div className="text-[10px] text-white/40">Estimated days to retail after acquisition</div>
        </KVStack>
      </div>
    </Section>
  );
}


// ===================================================================
// Section 5 — Pipeline control
// ===================================================================
function PipelineSection({ v, staffUsers = [], currentUser, onPatch }) {
  const [followUp, setFollowUp] = useState(
    v.next_follow_up_at ? v.next_follow_up_at.slice(0, 16) : ""
  );
  useEffect(() => {
    setFollowUp(v.next_follow_up_at ? v.next_follow_up_at.slice(0, 16) : "");
  }, [v.id, v.next_follow_up_at]);

  // Active staff only — inactive users are kept in the dropdown if they're
  // already assigned (so the operator can see who legacy lead is on).
  const activeStaff = staffUsers.filter(u => u.status !== "inactive" || u.id === v.assigned_to_user_id);

  const assignToMe = () => {
    if (!currentUser?.id) return;
    onPatch({ assigned_to_user_id: currentUser.id });
    toast.success(`Assigned to you`);
  };

  const onAssignChange = (uid) => {
    onPatch({ assigned_to_user_id: uid || "" });
    toast.success(uid ? "Assigned" : "Unassigned");
  };

  const createdBy = v.created_by?.name || v.created_by?.phone || (v.user_id ? "Seller" : "—");
  const lastUpdatedBy = v.last_updated_by?.name || v.last_updated_by?.phone || "—";
  // Source-of-truth for the visible owner: prefer the canonical user_id +
  // staff list lookup so the tile updates instantly after an optimistic PATCH
  // (before the backend mirror of `assigned_executive` arrives).
  const assignedStaff = v.assigned_to_user_id
    ? staffUsers.find(u => u.id === v.assigned_to_user_id)
    : null;
  const ownerLabel = assignedStaff?.name || assignedStaff?.phone || v.assigned_executive || "Unassigned";

  return (
    <Section title="Pipeline Control">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <KVStack k="Stage">
          <StagePicker stage={v.acquisition_stage || "new_lead"} onPick={(s) => onPatch({ acquisition_stage: s })} testid={`pipeline-stage-${v.id}`}/>
        </KVStack>
        <KVStack k="Assigned To">
          <div className="flex gap-1">
            <select
              data-testid={`assigned-to-${v.id}`}
              value={v.assigned_to_user_id || ""}
              onChange={(e) => onAssignChange(e.target.value)}
              className="qd-input-block flex-1 text-xs py-1.5 font-mono-data"
            >
              <option value="">Unassigned</option>
              {activeStaff.map(u => (
                <option key={u.id} value={u.id}>
                  {u.name || u.phone}{u.status === "inactive" ? " (inactive)" : ""}
                </option>
              ))}
            </select>
            {currentUser?.id && v.assigned_to_user_id !== currentUser.id && (
              <button
                onClick={assignToMe}
                data-testid={`assign-to-me-${v.id}`}
                className="btn-secondary text-[10px] py-1.5 px-2 whitespace-nowrap"
                title="Assign to me"
              >Me</button>
            )}
          </div>
        </KVStack>
        <KVStack k="Follow-Up Date">
          <div className="flex gap-1">
            <input type="datetime-local" value={followUp} onChange={(e) => setFollowUp(e.target.value)}
              data-testid={`followup-${v.id}`} className="qd-input-block flex-1 text-sm py-1.5"/>
            <button onClick={() => {
                onPatch({ next_follow_up_at: followUp ? new Date(followUp).toISOString() : null });
                toast.success("Follow-up set");
              }} data-testid={`followup-save-${v.id}`} className="btn-secondary text-[10px] py-1.5 px-2">Save</button>
          </div>
        </KVStack>
      </div>

      {/* Ownership & accountability strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3 text-[10px] font-mono-data tracking-wider">
        <div className="bg-[#0E0E0E] px-2 py-1.5 border-l-2 border-[#2A2A2A]" data-testid={`lead-created-by-${v.id}`}>
          <div className="text-white/40 uppercase tracking-widest text-[9px]">Created By</div>
          <div className="text-white/85 truncate">{createdBy}</div>
          <div className="text-white/35">{v.created_at ? `${timeAgo(v.created_at)} ago` : ""}</div>
        </div>
        <div className="bg-[#0E0E0E] px-2 py-1.5 border-l-2 border-[#2A2A2A]" data-testid={`lead-updated-by-${v.id}`}>
          <div className="text-white/40 uppercase tracking-widest text-[9px]">Last Updated</div>
          <div className="text-white/85 truncate">{lastUpdatedBy}</div>
          <div className="text-white/35">{v.last_updated_at ? `${timeAgo(v.last_updated_at)} ago` : ""}</div>
        </div>
        <div className="bg-[#0E0E0E] px-2 py-1.5 border-l-2 border-[#2A2A2A] col-span-2 sm:col-span-1" data-testid={`lead-assigned-${v.id}`}>
          <div className="text-white/40 uppercase tracking-widest text-[9px]">Owner</div>
          <div className="text-white/85 truncate">{ownerLabel}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={() => { onPatch({ acquisition_stage: "won" }); toast.success("Marked WON"); }}
          data-testid={`mark-won-${v.id}`} className="btn-secondary text-[10px] py-1.5 px-3" style={{ color: "#22C55E" }}>Mark Won</button>
        <button onClick={() => { onPatch({ acquisition_stage: "lost" }); toast.success("Marked LOST"); }}
          data-testid={`mark-lost-${v.id}`} className="btn-secondary text-[10px] py-1.5 px-3" style={{ color: "#5A5A5A" }}>Mark Lost</button>
        <button onClick={() => { onPatch({ archived: true }); toast.success("Archived"); }}
          data-testid={`archive-${v.id}`} className="btn-secondary text-[10px] py-1.5 px-3 inline-flex items-center gap-1">
          <Archive size={11} weight="bold" />Archive
        </button>
      </div>
    </Section>
  );
}

// ===================================================================
// Section 6 — Notes (chronological)
// ===================================================================
function NotesSection({ v, onPatch }) {
  const [note, setNote] = useState("");
  const addNote = async () => {
    if (!note.trim()) return;
    await onPatch({ note: note.trim() });
    setNote("");
    toast.success("Note added");
  };
  const notes = v.notes || [];
  return (
    <Section title={`Internal Notes · ${notes.length}`}>
      <div className="space-y-1.5 mb-3 max-h-48 overflow-y-auto">
        {notes.slice().reverse().map((n, i) => (
          <div key={`${n.at}-${i}`} className="border-l-2 border-[#2A2A2A] pl-3 py-1">
            <div className="text-[10px] font-mono-data tracking-widest text-white/40">
              {n.by || "exec"} · {timeAgo(n.at)} ago
            </div>
            <div className="text-xs text-white/85">{n.text}</div>
          </div>
        ))}
        {notes.length === 0 && <div className="text-xs text-white/40">No notes yet.</div>}
      </div>
      <div className="flex gap-2">
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Quick note…"
          data-testid={`note-input-${v.id}`} className="qd-input-block flex-1 text-xs py-1.5"
          onKeyDown={(e) => { if (e.key === "Enter") addNote(); }}/>
        <button onClick={addNote} data-testid={`note-add-${v.id}`} className="btn-secondary text-[10px] py-1.5 px-3">Add</button>
      </div>
    </Section>
  );
}

// ===================================================================
// Section 7 — Suggested buyers (compact list)
// ===================================================================
function SuggestedBuyersSection({ v }) {
  const [matches, setMatches] = useState(null);
  useEffect(() => {
    api.get(`/admin/matching/for-vehicle/${v.id}`)
      .then(r => setMatches(r.data.suggestions || []))
      .catch(() => setMatches([]));
  }, [v.id]);
  if (matches === null) return <Section title="Suggested Buyers"><div className="text-xs text-white/40">Loading…</div></Section>;
  return (
    <Section title={`Suggested Buyers · ${matches.length}`}>
      {matches.length === 0
        ? <div className="text-xs text-white/40">No matching buyer requirements yet.</div>
        : <div className="space-y-0.5">
            {matches.slice(0, 6).map((m, i) => (
              <div key={i} className="flex items-center justify-between gap-3 py-1 px-2 bg-[#0E0E0E] text-xs font-mono-data">
                <div className="truncate text-white">{m.requirement?.full_name || "Unnamed"}</div>
                <div className="text-white/55 truncate hidden sm:block">{m.requirement?.contact_phone || "—"}</div>
                <div className="text-white/55 hidden md:block">{fmtINR(m.requirement?.budget_min)}–{fmtINR(m.requirement?.budget_max)}</div>
                <div className="text-[#FF1E1E] font-display font-bold w-12 text-right">{m.match_pct}%</div>
              </div>
            ))}
          </div>}
    </Section>
  );
}

// ===================================================================
// Section 8 — Audit history (chronological pipeline events)
// ===================================================================

// ===================================================================
// OfferHistorySection — Renders every offer fired for this vehicle.
// Each row: Offer Type · Amount · Date · WhatsApp Status · Message ID
// Status badge color flows: pending (white) → submitted/sent (amber) →
// delivered (cyan) → read (emerald) | failed (red).
// ===================================================================
function OfferHistorySection({ v }) {
  const [items, setItems] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(() => {
    setRefreshing(true);
    api.get(`/admin/vehicles/${v.id}/offer-history`)
      .then(r => setItems(r.data?.offers || []))
      .catch(() => setItems([]))
      .finally(() => setRefreshing(false));
  }, [v.id]);

  useEffect(() => { load(); }, [load]);

  // Poll once after 8s so the operator sees status flips (submitted → sent →
  // delivered) without manually refreshing.
  useEffect(() => {
    if (!items || items.length === 0) return;
    const t = setTimeout(load, 8000);
    return () => clearTimeout(t);
  }, [items, load]);

  return (
    <Section title={`Offer History · ${items ? items.length : "…"}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] text-white/40 tracking-wider uppercase">PWA + WhatsApp dispatches</div>
        <button
          onClick={load}
          disabled={refreshing}
          data-testid={`offer-history-refresh-${v.id}`}
          className="btn-secondary text-[10px] py-1 px-2 inline-flex items-center gap-1 disabled:opacity-50"
        >
          <ArrowClockwise size={10} weight="bold" /> {refreshing ? "…" : "Refresh"}
        </button>
      </div>
      {items === null && <div className="text-xs text-white/40">Loading…</div>}
      {items && items.length === 0 && (
        <div className="text-xs text-white/40 py-3" data-testid={`offer-history-empty-${v.id}`}>
          No offers sent yet. Use the Negotiation panel to send your first.
        </div>
      )}
      {items && items.length > 0 && (
        <div className="space-y-1.5" data-testid={`offer-history-${v.id}`}>
          {items.map(o => <OfferHistoryRow key={o.id} o={o} />)}
        </div>
      )}
    </Section>
  );
}

function OfferHistoryRow({ o }) {
  const status = (o.status || "pending").toLowerCase();
  const STATUS_COLOR = {
    pending:   { fg: "#FFFFFF", bg: "rgba(255,255,255,0.10)" },
    submitted: { fg: "#F5A623", bg: "rgba(245,166,35,0.12)" },
    enqueued:  { fg: "#F5A623", bg: "rgba(245,166,35,0.12)" },
    sent:      { fg: "#F5A623", bg: "rgba(245,166,35,0.18)" },
    delivered: { fg: "#6FA8DC", bg: "rgba(111,168,220,0.15)" },
    read:      { fg: "#34D399", bg: "rgba(52,211,153,0.15)" },
    failed:    { fg: "#FF1E1E", bg: "rgba(255,30,30,0.15)" },
  };
  const col = STATUS_COLOR[status] || STATUS_COLOR.pending;
  const isUpdated = o.kind === "updated";
  return (
    <div
      className="bg-[#0E0E0E] border border-[#1F1F1F] px-2.5 py-2 grid grid-cols-[1fr_auto] gap-2"
      data-testid={`offer-history-row-${o.id}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 text-[11px]">
          <span className="font-display tracking-wider uppercase text-white/85">
            {isUpdated ? "Updated Offer" : "Initial Offer"}
          </span>
          <span className="text-white/30">·</span>
          <span className="font-display text-white">{fmtINR(o.amount || 0)}</span>
        </div>
        <div className="text-[10px] font-mono-data text-white/50 mt-0.5 truncate">
          {o.sent_at ? new Date(o.sent_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}
          {o.sent_by_name && <span className="text-white/35"> · by {o.sent_by_name}</span>}
        </div>
        {o.message_id && (
          <div className="text-[9px] font-mono-data text-white/30 mt-0.5 truncate" title={o.message_id}>
            id: {o.message_id}
          </div>
        )}
        {status === "failed" && o.failure_reason && (
          <div className="text-[10px] text-[#FF1E1E]/85 mt-0.5 truncate" title={o.failure_reason}>
            {o.failure_reason}
          </div>
        )}
      </div>
      <span
        className="self-start text-[9px] tracking-widest uppercase font-bold px-2 py-1 border whitespace-nowrap"
        style={{ color: col.fg, background: col.bg, borderColor: col.fg + "55" }}
        data-testid={`offer-status-${o.id}`}
      >
        {status}
      </span>
    </div>
  );
}


function AuditHistorySection({ v }) {
  const [activity, setActivity] = useState(null);
  useEffect(() => {
    api.get(`/admin/vehicles/${v.id}/activity`)
      .then(r => setActivity(r.data))
      .catch(() => setActivity({ history: v.acquisition_history || [], audit_logs: [] }));
  }, [v.id]);

  // Merge stage history + audit logs into one chronological feed.
  const events = useMemo(() => {
    if (!activity) return [];
    const stageEvents = (activity.history || []).map(e => ({
      kind: "stage",
      at: e.at,
      actor: e.by,
      label: (e.stage || "").replace(/_/g, " ").toUpperCase(),
      detail: e.note,
    }));
    const logEvents = (activity.audit_logs || []).map(l => ({
      kind: "log",
      at: l.created_at,
      actor: l.actor_name || l.actor_phone || "system",
      label: (l.action || "").replace(/_/g, " ").toUpperCase(),
      detail: Array.isArray(l.extra?.fields)
        ? `changed: ${l.extra.fields.join(", ")}`
        : (l.extra?.note || ""),
    }));
    return [...stageEvents, ...logEvents].sort(
      (a, b) => new Date(b.at) - new Date(a.at)
    );
  }, [activity]);

  return (
    <Section title={`Activity Timeline · ${events.length}`}>
      <div className="space-y-1 max-h-56 overflow-y-auto font-mono-data text-[11px]" data-testid={`activity-timeline-${v.id}`}>
        {activity === null && <div className="text-xs text-white/40">Loading…</div>}
        {activity !== null && events.map((e, i) => (
          <div key={i} className="flex items-start gap-3 py-1 border-b border-[#1A1A1A]">
            <div
              className="w-1 self-stretch shrink-0"
              style={{ background: e.kind === "stage" ? "#FF1E1E" : "#3A3A3A" }}
            />
            <div className="text-white/40 w-24 shrink-0 tracking-wider uppercase text-[10px]">{e.label}</div>
            <div className="flex-1 min-w-0">
              <div className="text-white/75 truncate">{e.detail || "—"}</div>
              <div className="text-white/35 text-[10px]">{e.actor}</div>
            </div>
            <div className="text-white/35 w-16 text-right shrink-0">{e.at ? `${timeAgo(e.at)} ago` : ""}</div>
          </div>
        ))}
        {activity !== null && events.length === 0 && <div className="text-xs text-white/40">No activity yet.</div>}
      </div>
    </Section>
  );
}

// ===================================================================
// Shared mini-components
// ===================================================================
function Section({ title, children }) {
  return (
    <section>
      <div className="qd-overline mb-2">{title}</div>
      {children}
    </section>
  );
}
function KV({ k, v }) {
  return (
    <div className="flex justify-between border-b border-[#1A1A1A] py-1">
      <span className="text-white/40 uppercase tracking-widest text-[10px]">{k}</span>
      <span className="text-white text-right truncate ml-2">{v}</span>
    </div>
  );
}
function KVStack({ k, children }) {
  return (
    <div>
      <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/40 mb-1">{k}</div>
      {children}
    </div>
  );
}

function StagePicker({ stage, onPick, testid }) {
  return (
    <select value={stage}
      onChange={(e) => onPick(e.target.value)}
      data-testid={testid}
      className="bg-[#0F0F0F] border border-[#2A2A2A] text-[10px] tracking-wider uppercase text-white/85 px-2 py-1.5 w-full font-mono-data focus:outline-none focus:border-[#FF1E1E]"
    >
      {ACQUISITION_STAGES.map(s => (
        <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
      ))}
    </select>
  );
}

// Export StageBadge / AgeBadge re-exports kept for any external consumers (not needed here).
export { StageBadge, AgeBadge };
